import React, { useState, useMemo, useEffect } from 'react';
import { LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer, PieChart, Pie, Cell, BarChart, Bar } from 'recharts';
import { Home, BookOpen, Calculator, Briefcase, TrendingUp, Search, Award, Newspaper, Star, Euro, Target, Clock, BarChart2, Info } from 'lucide-react';

// --- MOCK DATA & UTILITIES ---
function useLocalStorage(key, initialValue) {
  const [storedValue, setStoredValue] = useState(() => {
    try {
      const item = window.localStorage.getItem(key);
      return item ? JSON.parse(item) : initialValue;
    } catch (error) {
      return initialValue;
    }
  });
  useEffect(() => {
    try { window.localStorage.setItem(key, JSON.stringify(storedValue)); } catch (e) {}
  }, [key, storedValue]);
  return [storedValue, setStoredValue];
}

// --- I18N (ALB / ENG) ---
const translations = {
  ALB: {
    Dashboard: 'Paneli',
    'Learning Modules': 'Modulet Mësimore',
    Toolkit: 'Mjetet',
    'Case Studies': 'Studime Rasti',
    'The Simulation': 'Simulimi',
    Glossary: 'Fjalori',
    Top: 'Lart',
    'Reset Data': 'Rivendos të Dhënat',
    'Market Ticker': 'Treguesi i Tregut',
    Refresh: 'Rifresko',
    'Your Portfolio': 'Portofoli Juaj',
    Symbol: 'Simboli',
    Qty: 'Sasia',
    'Avg Cost': 'Kosto Mesatare',
    Price: 'Çmimi',
    'P/L': 'Fitim/Humbje',
    Buy: 'Bli',
    Sell: 'Shit',
    Quantity: 'Sasia',
    'No holdings. Make your first trade!': 'S’ka pozicione. Bëni tregtinë tuaj të parë!',
    Cash: 'Para',
    Value: 'Vlera',
    'Allocation by Symbol (%)': 'Ndarja sipas Simbolit (%)',
    'No positions to chart yet. Buy at least one symbol to see your allocation.': 'Ende s’ka pozicione për grafik. Blini të paktën një simbol për të parë ndarjen.',
    SimulationTitle: '"Bursa" - Investimi në Tregun e Aksioneve',
    'Fact of the Day': 'Fakti i Ditës',
    'Market Insights': 'Vëzhgime nga Tregu',
    'Quick Tips': 'Këshilla të Shpejta',
    'Your Learning Path': 'Rruga Juaj e Mësimit',
    'Go to Simulator': 'Shko te Simulimi',
    'Open Toolkit': 'Hap Mjetet',
    'Continue Modules': 'Vazhdo Modulet',
    'Try Case Studies': 'Provo Studimet e Rastit',
    'Built for educational purposes by': 'Ndërtuar për qëllime edukative nga',
    'Use the Toolkit to compute NPV/IRR before accepting a project.': 'Përdorni Mjetet për të llogaritur NPV/IRR para se të pranoni një projekt.',
    'Estimate your firm’s WACC for a consistent discount rate.': 'Vlerësoni WACC-n e kompanisë për një normë zbritjeje të qëndrueshme.',
    'In the Simulator, practice position sizing and diversification.': 'Në Simulim, praktikoni madhësinë e pozicionit dhe diversifikimin.',
    'Pick up where you left off in the curriculum.': 'Vazhdoni aty ku e latë në kurrikul.',
    'Use NPV/IRR, WACC, DDM, and more.': 'Përdorni NPV/IRR, WACC, DDM, dhe të tjera.',
    'Practice trading with live market data.': 'Praktikoni tregtimin me të dhëna të drejtpërdrejta.',
    'Work through guided corporate finance cases.': 'Punoni me raste të udhëhequra të financës korporative.',
    'Interactive Learning Modules': 'Modulet Mësimore Interaktive',
    'Time Value of Money': 'Vlera Kohore e Parasë',
    'Capital Budgeting': 'Buxhetimi i Kapitalit',
    'Risk and Return': 'Rreziku dhe Kthimi',
    'Cost of Capital': 'Kostoja e Kapitalit',
    'Capital Structure': 'Struktura e Kapitalit',
    'Financial Toolkit': 'Mjetet Financiare',
    'Interactive calculators with formulas and tips. All computations run locally in your browser.': 'Kalkulatorë interaktivë me formula dhe këshilla. Të gjitha llogaritjet kryhen lokalisht në shfletuesin tuaj.',
    'Search terms or definitions': 'Kërkoni terma ose përkufizime',
    'No results': 'Nuk u gjet asnjë rezultat',
    'Glossary': 'Fjalori',
    'Definition:': 'Përkufizimi:',
    'Formula:': 'Formula:',
    'Example:': 'Shembull:',
    'Go to related module': 'Shko te moduli përkatës',
    'No term selected.': 'Asnjë term i zgjedhur.',
    'NPV & IRR': 'NPV & IRR',
    'WACC': 'WACC',
    'Loan Amortization': 'Amortizimi i Kredisë',
    'Bond Valuation': 'Vlerësimi i Obligacioneve',
    'DDM (Dividend Discount)': 'DDM (Zbritja e Dividentit)',
    'Beta (CAPM)': 'Beta (CAPM)',
    'Payback': 'Periudha e Kthimit',
    'Discounted Payback': 'Periudha e Kthimit e Skontuar/Zbritur',
    'AAR': 'AAR',
    'PI': 'PI',
    'NPV & IRR Calculator': 'Kalkulatori NPV & IRR',
    'WACC Calculator': 'Kalkulatori WACC',
    'Profitability Index (PI)': 'Indeksi i Fitimprurjes (PI)',
    'Initial Investment (CF0)': 'Investimi Fillestar (CF0)',
    'Discount Rate (%)': 'Norma e Zbritjes/Skontimit (%)',
    'Cash Flows': 'Rrjedhjet e Parasë',
    Remove: 'Hiq',
    'Add Cash Flow': 'Shto Rrjedhje Parash',
    Achievements: 'Arritjet',
    'First Trade': 'Tregtia e Parë',
    '3+ Symbols Diversified': '3+ Simbole të Diversifikuara',
    'Portfolio ≥ $100,500': 'Portofol ≥ $100,500',
    'Market Value of Equity (E)': 'Vlera e Tregut e Kapitalit (E)',
    'Market Value of Debt (D)': 'Vlera e Tregut e Borxhit (D)',
    'Cost of Equity (Re, %)': 'Kostoja e Kapitalit (Re, %)',
    'Cost of Debt (Rd, %)': 'Kostoja e Borxhit (Rd, %)',
    'Tax Rate (T, %)': 'Norma e Tatimit (T, %)',
    // Facts (Dashboard)
    'The concept of interest dates back to ancient Mesopotamia around 3000 BC.': 'Koncepti i interesit daton që nga Mesopotamia e lashtë rreth vitit 3000 p.e.s.',
    'The first publicly traded company was the Dutch East India Company, established in 1602.': 'Kompania e parë e listuar publikisht ishte Kompania Holandeze e Indive Lindore, themeluar në vitin 1602.',
    'Warren Buffett bought his first stock at age 11.': 'Warren Buffett bleu aksionin e tij të parë në moshën 11 vjeçare.',
    "The term 'bull market' and 'bear market' are thought to originate from the way these animals attack.": "Termat 'treg dem' dhe 'treg ari' mendohet se vijnë nga mënyra se si sulmojnë këto kafshë.",
    'Only about 55% of American adults invest in the stock market.': 'Vetëm rreth 55% e të rriturve amerikanë investojnë në tregun e aksioneve.',

    // Market Insights bullets
    'Live quotes on this dashboard are powered by TwelveData and refresh hourly or on load.': 'Kuotat e drejtpërdrejta në këtë panel mundësohen nga TwelveData dhe rifreskohen çdo orë ose gjatë ngarkimit.',
    'Big intraday swings often coincide with earnings, guidance updates, or macro data releases.': 'Luhatjet e mëdha brenda ditës shpesh përkojnë me raportet e fitimeve, përditësimet e udhëzimeve ose lëshimet e të dhënave makro.',
    'Diversify across sectors to reduce unsystematic risk; monitor position sizes versus risk tolerance.': 'Diversifikoni nëpër sektorë për të ulur rrezikun e pavarur; monitoroni madhësitë e pozicioneve kundrejt tolerancës suaj ndaj rrezikut.',

    // Case Studies (Deal Room)
    'The "Deal Room" - Guided Case Study': '“Dhoma e Marrëveshjeve” - Studim Rasti i Udhëzuar',
    'Case Study:': 'Studim Rasti:',
    'Step 1: Review Statements (Inline)': 'Hapi 1: Shqyrtoni Pasqyrat (Në vijim)',
    'Income Statement': 'Pasqyra e të Ardhurave',
    'Balance Sheet': 'Bilanci i Gjendjes',
    'Cash Flow Statement': 'Pasqyra e Flukseve Monetare',
    'Continue to Analysis': 'Vazhdoni te Analiza',
    'Step 2: Perform Analysis': 'Hapi 2: Kryeni Analizën',
    'Open the Toolkit and use NPV & IRR (or WACC if needed). Assume discount rate 10% unless you compute one.': 'Hapni Mjetet dhe përdorni NPV & IRR (ose WACC nëse nevojitet). Supozoni normë zbritjeje 10% përveç nëse llogaritni një tjetër.',
    'Compute NPV using project flows:': 'Llogaritni NPV duke përdorur rrjedhat e projektit:',
    'Estimate IRR; note any multiple IRR issues if signs change.': 'Vlerësoni IRR; vini re probleme të IRR të shumëfishtë nëse ndryshojnë shenjat.',
    'Consider qualitative factors: strategy, risk, and resources.': 'Konsideroni faktorët cilësorë: strategjinë, rrezikun dhe burimet.',
    'Proceed to Recommendation': 'Vazhdoni te Rekomandimi',
    'Step 3: Submit Recommendation': 'Hapi 3: Dorëzoni Rekomandimin',
    'Your Recommendation': 'Rekomandimi Juaj',
    'State your decision (Accept/Reject) and justify with NPV/IRR and qualitative factors.': 'Shkruani vendimin tuaj (Prano/Refuzo) dhe arsyetoni me NPV/IRR dhe faktorë cilësorë.',
    Submit: 'Dërgo',
    Clear: 'Pastro',
    'Step 4: Solution Walkthrough': 'Hapi 4: Shpjegimi i Zgjidhjes',
    'An acceptable solution could be:': 'Një zgjidhje e pranueshme mund të jetë:',
    'Restart Case': 'Rinisni Rastin',
    'Edit Recommendation': 'Ndrysho Rekomandimin',
    Progress: 'Përparimi',
    Tips: 'Këshilla',
    'Step of 4': 'Hapi {step} nga 4',
    'Use the Toolkit to ensure consistency of assumptions.': 'Përdorni Mjetet për të siguruar qëndrueshmërinë e supozimeve.',
    'Cross-check cash flow timing and signs.': 'Kontrolloni kohën dhe shenjat e flukseve monetare.',
    'Document key assumptions in your write-up.': 'Dokumentoni supozimet kryesore në shkrimin tuaj.',

    // Table headers (display only)
    Year: 'Viti',
    Revenue: 'Të Ardhurat',
    COGS: 'Kosto e Mallrave të Shitura (COGS)',
    Opex: 'Shpenzimet Operative',
    Dep: 'Zhvlerësimi',
    Cash: 'Para',
    Debt: 'Borgji',
    Equity: 'Kapitali',
    Operating: 'Operative',
    Investing: 'Investive',
    Financing: 'Financuese'
  },
  ENG: {
    Dashboard: 'Dashboard',
    'Learning Modules': 'Learning Modules',
    Toolkit: 'Toolkit',
    'Case Studies': 'Case Studies',
    'The Simulation': 'The Simulation',
    Glossary: 'Glossary',
    Top: 'Top',
    'Reset Data': 'Reset Data',
    'Market Ticker': 'Market Ticker',
    Refresh: 'Refresh',
    'Your Portfolio': 'Your Portfolio',
    Symbol: 'Symbol',
    Qty: 'Qty',
    'Avg Cost': 'Avg Cost',
    Price: 'Price',
    'P/L': 'P/L',
    Buy: 'Buy',
    Sell: 'Sell',
    Quantity: 'Quantity',
    'No holdings. Make your first trade!': 'No holdings. Make your first trade!',
    Cash: 'Cash',
    Value: 'Value',
    'Allocation by Symbol (%)': 'Allocation by Symbol (%)',
    'No positions to chart yet. Buy at least one symbol to see your allocation.': 'No positions to chart yet. Buy at least one symbol to see your allocation.',
    SimulationTitle: '"The Exchange" - Stock Market Investment',
    'Fact of the Day': 'Fact of the Day',
    'Market Insights': 'Market Insights',
    'Quick Tips': 'Quick Tips',
    'Your Learning Path': 'Your Learning Path',
    'Go to Simulator': 'Go to Simulator',
    'Open Toolkit': 'Open Toolkit',
    'Continue Modules': 'Continue Modules',
    'Try Case Studies': 'Try Case Studies',
    'Built for educational purposes by': 'Built for educational purposes by',
    'Use the Toolkit to compute NPV/IRR before accepting a project.': 'Use the Toolkit to compute NPV/IRR before accepting a project.',
    'Estimate your firm’s WACC for a consistent discount rate.': 'Estimate your firm’s WACC for a consistent discount rate.',
    'In the Simulator, practice position sizing and diversification.': 'In the Simulator, practice position sizing and diversification.',
    'Pick up where you left off in the curriculum.': 'Pick up where you left off in the curriculum.',
    'Use NPV/IRR, WACC, DDM, and more.': 'Use NPV/IRR, WACC, DDM, and more.',
    'Practice trading with live market data.': 'Practice trading with live market data.',
    'Work through guided corporate finance cases.': 'Work through guided corporate finance cases.',
    'Interactive Learning Modules': 'Interactive Learning Modules',
    'Time Value of Money': 'Time Value of Money',
    'Capital Budgeting': 'Capital Budgeting',
    'Risk and Return': 'Risk and Return',
    'Cost of Capital': 'Cost of Capital',
    'Capital Structure': 'Capital Structure',
    'Financial Toolkit': 'Financial Toolkit',
    'Interactive calculators with formulas and tips. All computations run locally in your browser.': 'Interactive calculators with formulas and tips. All computations run locally in your browser.',
    'Search terms or definitions': 'Search terms or definitions',
    'No results': 'No results',
    'Glossary': 'Glossary',
    'Definition:': 'Definition:',
    'Formula:': 'Formula:',
    'Example:': 'Example:',
    'Go to related module': 'Go to related module',
    'No term selected.': 'No term selected.',
    'NPV & IRR': 'NPV & IRR',
    'WACC': 'WACC',
    'Loan Amortization': 'Loan Amortization',
    'Bond Valuation': 'Bond Valuation',
    'DDM (Dividend Discount)': 'DDM (Dividend Discount)',
    'Beta (CAPM)': 'Beta (CAPM)',
    'Payback': 'Payback',
    'Discounted Payback': 'Discounted Payback',
    'AAR': 'AAR',
    'PI': 'PI',
    'NPV & IRR Calculator': 'NPV & IRR Calculator',
    'WACC Calculator': 'WACC Calculator',
    'Profitability Index (PI)': 'Profitability Index (PI)',
    'Initial Investment (CF0)': 'Initial Investment (CF0)',
    'Discount Rate (%)': 'Discount Rate (%)',
    'Cash Flows': 'Cash Flows',
    Remove: 'Remove',
    'Add Cash Flow': 'Add Cash Flow',
    Achievements: 'Achievements',
    'First Trade': 'First Trade',
    '3+ Symbols Diversified': '3+ Symbols Diversified',
    'Portfolio ≥ $100,500': 'Portfolio ≥ $100,500',
    'Market Value of Equity (E)': 'Market Value of Equity (E)',
    'Market Value of Debt (D)': 'Market Value of Debt (D)',
    'Cost of Equity (Re, %)': 'Cost of Equity (Re, %)',
    'Cost of Debt (Rd, %)': 'Cost of Debt (Rd, %)',
    'Tax Rate (T, %)': 'Tax Rate (T, %)',
    // Facts (Dashboard)
    'The concept of interest dates back to ancient Mesopotamia around 3000 BC.': 'The concept of interest dates back to ancient Mesopotamia around 3000 BC.',
    'The first publicly traded company was the Dutch East India Company, established in 1602.': 'The first publicly traded company was the Dutch East India Company, established in 1602.',
    'Warren Buffett bought his first stock at age 11.': 'Warren Buffett bought his first stock at age 11.',
    "The term 'bull market' and 'bear market' are thought to originate from the way these animals attack.": "The term 'bull market' and 'bear market' are thought to originate from the way these animals attack.",
    'Only about 55% of American adults invest in the stock market.': 'Only about 55% of American adults invest in the stock market.',

    // Market Insights bullets
    'Live quotes on this dashboard are powered by TwelveData and refresh hourly or on load.': 'Live quotes on this dashboard are powered by TwelveData and refresh hourly or on load.',
    'Big intraday swings often coincide with earnings, guidance updates, or macro data releases.': 'Big intraday swings often coincide with earnings, guidance updates, or macro data releases.',
    'Diversify across sectors to reduce unsystematic risk; monitor position sizes versus risk tolerance.': 'Diversify across sectors to reduce unsystematic risk; monitor position sizes versus risk tolerance.',

    // Case Studies (Deal Room)
    'The "Deal Room" - Guided Case Study': 'The "Deal Room" - Guided Case Study',
    'Case Study:': 'Case Study:',
    'Step 1: Review Statements (Inline)': 'Step 1: Review Statements (Inline)',
    'Income Statement': 'Income Statement',
    'Balance Sheet': 'Balance Sheet',
    'Cash Flow Statement': 'Cash Flow Statement',
    'Continue to Analysis': 'Continue to Analysis',
    'Step 2: Perform Analysis': 'Step 2: Perform Analysis',
    'Open the Toolkit and use NPV & IRR (or WACC if needed). Assume discount rate 10% unless you compute one.': 'Open the Toolkit and use NPV & IRR (or WACC if needed). Assume discount rate 10% unless you compute one.',
    'Compute NPV using project flows:': 'Compute NPV using project flows:',
    'Estimate IRR; note any multiple IRR issues if signs change.': 'Estimate IRR; note any multiple IRR issues if signs change.',
    'Consider qualitative factors: strategy, risk, and resources.': 'Consider qualitative factors: strategy, risk, and resources.',
    'Proceed to Recommendation': 'Proceed to Recommendation',
    'Step 3: Submit Recommendation': 'Step 3: Submit Recommendation',
    'Your Recommendation': 'Your Recommendation',
    'State your decision (Accept/Reject) and justify with NPV/IRR and qualitative factors.': 'State your decision (Accept/Reject) and justify with NPV/IRR and qualitative factors.',
    Submit: 'Submit',
    Clear: 'Clear',
    'Step 4: Solution Walkthrough': 'Step 4: Solution Walkthrough',
    'An acceptable solution could be:': 'An acceptable solution could be:',
    'Restart Case': 'Restart Case',
    'Edit Recommendation': 'Edit Recommendation',
    Progress: 'Progress',
    Tips: 'Tips',
    'Step of 4': 'Step {step} of 4',
    'Use the Toolkit to ensure consistency of assumptions.': 'Use the Toolkit to ensure consistency of assumptions.',
    'Cross-check cash flow timing and signs.': 'Cross-check cash flow timing and signs.',
    'Document key assumptions in your write-up.': 'Document key assumptions in your write-up.',

    // Table headers (display only)
    Year: 'Year',
    Revenue: 'Revenue',
    COGS: 'COGS',
    Opex: 'Opex',
    Dep: 'Dep',
    Cash: 'Cash',
    Debt: 'Debt',
    Equity: 'Equity',
    Operating: 'Operating',
    Investing: 'Investing',
    Financing: 'Financing'
  }
};
const useI18n = (initial = 'ALB') => {
  const [lang, setLang] = useLocalStorage('lang', initial);
  const t = (key) => (translations[lang]?.[key] ?? translations.ENG[key] ?? key);
  return { lang, setLang, t };
};

// TwelveData API Key handling: env var + fallback constant
const DEFAULT_API_KEY = 'e4776c64f738403fbfd2cd7a2156709b';
const getApiKey = () => (process.env.REACT_APP_TWELVEDATA_API_KEY || process.env.TWELVEDATA_API_KEY || DEFAULT_API_KEY);

function logActivity(type, details = {}) {
  try {
    const list = JSON.parse(localStorage.getItem('activity_log') || '[]');
    list.unshift({ type, details, ts: new Date().toISOString() });
    localStorage.setItem('activity_log', JSON.stringify(list.slice(0, 500)));
  } catch {}
}

const modules = [
  { id: 1, title: 'Time Value of Money', icon: Clock, progress: 80, color: '#4A90E2' },
  { id: 2, title: 'Capital Budgeting', icon: Target, progress: 45, color: '#50E3C2' },
  { id: 3, title: 'Risk and Return', icon: TrendingUp, progress: 60, color: '#F5A623' },
  { id: 4, title: 'Cost of Capital', icon: Euro, progress: 20, color: '#BD10E0' },
  { id: 5, title: 'Capital Structure', icon: BarChart2, progress: 0, color: '#9013FE' },
];

const glossaryTerms = [
  { term: 'NPV', definition: 'Net present value is the present value of cash inflows minus the present value of cash outflows over a period of time.', formula: 'NPV = Σ (CF_t / (1+r)^t) - CF_0', example: 'Invest $1,000 now for $300 per year for 4 years at 10%.' },
  { term: 'IRR', definition: 'Internal rate of return is the discount rate that makes NPV equal to zero.', formula: 'NPV = 0 at r = IRR', example: 'Find r such that discounted cash flows equal initial investment.' },
  { term: 'WACC', definition: 'Weighted average cost of capital is the average rate a company is expected to pay to finance its assets.', formula: 'WACC = (E/V)*Re + (D/V)*Rd*(1-T)', example: '60% equity at 12%, 40% debt at 6%, tax 25%.' },
  { term: 'Time Value of Money', definition: 'Money available now is worth more than the same amount in the future due to its earning capacity.', formula: 'FV = P(1 + r/n)^(nt)', example: 'Invest $1,000 at 5% for 10 years compounded monthly.' },
  { term: 'CAPM', definition: 'Capital Asset Pricing Model relates expected return to risk via beta.', formula: 'E(Ri) = Rf + βi(E(Rm) − Rf)', example: 'If Rf=2%, β=1.2, market premium=5%, E(Ri)=8%.' },
  { term: 'Payback Period', definition: 'Time required to recover the cost of an investment.', formula: 'Years before recovery + (Unrecovered cost at start of year / Cash flow during year)', example: 'If $1,000 initial and $400 per year, payback occurs during year 3.' },
  { term: 'Dividend Yield', definition: 'Annual dividends per share divided by price per share.', formula: 'DY = DPS / Price', example: 'DPS $2, price $50, yield 4%.' },
  { term: 'Enterprise Value', definition: 'Value of firm operations available to all capital providers.', formula: 'EV = Equity Value + Debt − Cash', example: 'Market cap 100, Debt 40, Cash 10 -> EV 130.' },
  { term: 'EBITDA', definition: 'Earnings before interest, taxes, depreciation, and amortization; a proxy for operating cash flow.', formula: 'EBITDA = Revenue − COGS − Opex (ex-D&A)', example: 'Revenue 100, COGS 40, Opex 30 → EBITDA 30.' },
  { term: 'FCF', definition: 'Free Cash Flow is cash generated by operations after capital expenditures.', formula: 'FCF = CFO − Capex', example: 'Cash from operations 80, Capex 20 → FCF 60.' },
  { term: 'ROIC', definition: 'Return on invested capital measures efficiency at allocating capital.', formula: 'ROIC = NOPAT / Invested Capital', example: 'NOPAT 50, invested capital 500 → 10%.' },
  { term: 'P/E Ratio', definition: 'Price-to-earnings ratio compares a company’s share price to its earnings per share.', formula: 'P/E = Price per Share / EPS', example: 'Price $50, EPS $2 → P/E 25x.' },
  { term: 'Sharpe Ratio', definition: 'Risk-adjusted return metric.', formula: 'Sharpe = (Rp − Rf) / σp', example: 'Portfolio 10%, Rf 2%, σ 12% → 0.67.' },
  { term: 'Alpha', definition: 'Excess return versus a benchmark after adjusting for risk (beta).', formula: 'α = Rp − [Rf + β(Rm − Rf)]', example: 'If model predicts 8% and you deliver 9% → α ≈ 1%.' }
];

const financeFacts = [
  'The concept of interest dates back to ancient Mesopotamia around 3000 BC.',
  'The first publicly traded company was the Dutch East India Company, established in 1602.',
  'Warren Buffett bought his first stock at age 11.',
  "The term 'bull market' and 'bear market' are thought to originate from the way these animals attack.",
  'Only about 55% of American adults invest in the stock market.'
];

// --- LIVE PRICE LIST (Finnhub) ---
const TickerBar = ({ t = (s)=>s }) => {
  const apiKey = getApiKey();
  const DISPLAY = ['AAPL','MSFT','NVDA','TSLA','AMZN','GOOGL'];
  const displayTickers = DISPLAY; // lock to fixed list
  // Cache with migration from legacy key to preserve last-known-good quotes for instant render
  const CACHE_KEY = 'ticker_cache_v2';
  const [quotes, setQuotes] = useState(() => {
    try {
      const current = JSON.parse(localStorage.getItem(CACHE_KEY) || 'null');
      if (current && current.quotes) return current.quotes;
      const legacy = JSON.parse(localStorage.getItem('ticker_cache') || 'null');
      if (legacy && legacy.quotes) return legacy.quotes; // migrate silently
      return {};
    } catch {
      return {};
    }
  }); // {SYM: {c, dp}}
  const [lastTs, setLastTs] = useState(() => {
    try {
      const cached = JSON.parse(localStorage.getItem(CACHE_KEY) || '{}');
      return cached.ts || 0;
    } catch { return 0; }
  });

  // Manual fetch function (also used by Refresh button)
  const fetchOnce = async () => {
    try {
      const controller = new AbortController();
      const t = setTimeout(() => controller.abort(), 7000);
      const url = `/.netlify/functions/quotes?symbols=${encodeURIComponent(displayTickers.join(','))}`;
      const res = await fetch(url, { signal: controller.signal });
      const json = await res.json().catch(()=>({}));
      clearTimeout(t);
      const next = json && json.quotes ? json.quotes : {};
      setQuotes(prev => ({ ...prev, ...next }));
    } catch {}
  };

  // Persist to cache for instant next-load display
  useEffect(() => {
    try {
      const ts = Date.now();
      setLastTs(ts);
      localStorage.setItem(CACHE_KEY, JSON.stringify({ ts, quotes }));
    } catch {}
  }, [quotes]);

  // Fetch quotes via Netlify Function (batched); fetch on mount and every 60s
  useEffect(() => {
    let cancelled = false;
    async function load() {
      try {
        const controller = new AbortController();
        const t = setTimeout(() => controller.abort(), 7000);
        const url = `/.netlify/functions/quotes?symbols=${encodeURIComponent(displayTickers.join(','))}`;
        const res = await fetch(url, { signal: controller.signal });
        const json = await res.json().catch(()=>({}));
        clearTimeout(t);
        if (cancelled) return;
        const next = json && json.quotes ? json.quotes : {};
        setQuotes(prev => ({ ...prev, ...next }));
      } catch {}
    }
    if (displayTickers.length) {
      load();
      const id = setInterval(load, 60000); // refresh every 60s
      return () => { cancelled = true; clearInterval(id); };
    }
  }, [apiKey, displayTickers]);

  // Slim, moving ticker
  const items = displayTickers.map((s, idx) => {
    const q = quotes[s];
    const price = q?.c ?? null;
    const change = q?.dp ?? null;
    const up = change == null ? null : change >= 0;
    return (
      <div key={`${s}-${idx}`} className="flex items-baseline mx-6 text-xs sm:text-sm">
        <span className="font-semibold tracking-wide">{s}</span>
        <span className={`ml-2 font-semibold ${up===null? 'text-gray-700' : up ? 'text-green-700' : 'text-red-700'}`}>{price==null? '—' : ('$'+price.toFixed(2))}</span>
        <span className={`ml-2 ${up===null? 'text-gray-500' : up ? 'text-green-600' : 'text-red-600'}`}>{change==null? '' : `${change.toFixed(2)}%`}</span>
      </div>
    );
  });

  return (
    <div className="w-full border rounded-lg bg-white overflow-hidden">
      <div className="flex items-center justify-between px-3 pt-2 text-xs text-gray-500">
        <span>{t('Market Ticker')}</span>
        <button onClick={fetchOnce} className="px-2 py-1 rounded-md border text-gray-700 hover:bg-gray-50">{t('Refresh')}</button>
      </div>
      <div className="flex items-center py-2 px-2 whitespace-nowrap animate-marquee">
        {items}
        {items}
      </div>
    </div>
  );
};

// --- Additional Capital Budgeting Tools ---
const PaybackCalculator = () => {
  const [initial, setInitial] = useLocalStorage('payback_initial', 1000);
  const [flows, setFlows] = useLocalStorage('payback_flows', [300, 300, 300, 300]);
  const payback = useMemo(() => {
    let cum = -Number(initial);
    for (let i = 0; i < flows.length; i++) {
      cum += Number(flows[i]);
      if (cum >= 0) {
        const prevCum = cum - Number(flows[i]);
        const rem = -prevCum;
        return i + (rem / Number(flows[i]));
      }
    }
    return null;
  }, [initial, flows]);
  return (
    <div>
      <CardHeader title="Payback Period" />
      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        <div className="space-y-4">
          <div><label className="font-semibold">Initial Investment (CF0)</label><input type="number" value={initial} onChange={(e)=>setInitial(Number(e.target.value))} className="w-full mt-1 p-2 border rounded-lg"/></div>
          <div>
            <label className="font-semibold">Cash Flows</label>
            <div className="space-y-2 mt-1">
              {flows.map((cf,i)=> (
                <div key={i} className="flex items-center gap-2">
                  <input type="number" value={cf} onChange={(e)=>{ const c=[...flows]; c[i]=Number(e.target.value); setFlows(c);} } className="flex-1 p-2 border rounded-lg" />
                  <button onClick={()=>setFlows(flows.filter((_,idx)=>idx!==i))} className="text-red-600">Remove</button>
                </div>
              ))}
              <button onClick={()=>setFlows([...flows, 0])} className="text-blue-600 font-semibold">+ Add Cash Flow</button>
            </div>
          </div>
        </div>
        <div className="space-y-4">
          <div className="bg-blue-50 border-2 border-blue-200 rounded-xl p-4"><p className="text-gray-600">Payback</p><p className="text-3xl font-bold text-blue-700">{payback===null?'—':payback.toFixed(2)} years</p><p className="text-sm text-gray-500">Time required to recover CF0 (ignores time value of money)</p></div>
        </div>
      </div>
    </div>
  );
};

const DiscountedPaybackCalculator = () => {
  const [initial, setInitial] = useLocalStorage('dpb_initial', 1000);
  const [rate, setRate] = useLocalStorage('dpb_rate', 10);
  const [flows, setFlows] = useLocalStorage('dpb_flows', [300, 300, 300, 300]);
  const dpb = useMemo(() => {
    const r = rate/100; let cum = -Number(initial);
    for (let i=0;i<flows.length;i++){
      const pv = Number(flows[i]) / Math.pow(1+r, i+1);
      const before = cum; cum += pv;
      if (cum >= 0){
        const rem = -before; // needed after year i
        return i + (rem / pv);
      }
    }
    return null;
  }, [initial, rate, flows]);
  return (
    <div>
      <CardHeader title="Discounted Payback" />
      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        <div className="space-y-4">
          <div><label className="font-semibold">Initial Investment (CF0)</label><input type="number" value={initial} onChange={(e)=>setInitial(Number(e.target.value))} className="w-full mt-1 p-2 border rounded-lg"/></div>
          <div><label className="font-semibold">Discount Rate (%)</label><input type="number" step="0.1" value={rate} onChange={(e)=>setRate(Number(e.target.value))} className="w-full mt-1 p-2 border rounded-lg"/></div>
          <div>
            <label className="font-semibold">Cash Flows</label>
            <div className="space-y-2 mt-1">
              {flows.map((cf,i)=> (
                <div key={i} className="flex items-center gap-2">
                  <input type="number" value={cf} onChange={(e)=>{ const c=[...flows]; c[i]=Number(e.target.value); setFlows(c);} } className="flex-1 p-2 border rounded-lg" />
                  <button onClick={()=>setFlows(flows.filter((_,idx)=>idx!==i))} className="text-red-600">Remove</button>
                </div>
              ))}
              <button onClick={()=>setFlows([...flows, 0])} className="text-blue-600 font-semibold">+ Add Cash Flow</button>
            </div>
          </div>
        </div>
        <div className="space-y-4">
          <div className="bg-blue-50 border-2 border-blue-200 rounded-xl p-4"><p className="text-gray-600">Discounted Payback</p><p className="text-3xl font-bold text-blue-700">{dpb===null?'—':dpb.toFixed(2)} years</p><p className="text-sm text-gray-500">Time to recover CF0 using discounted cash flows</p></div>
        </div>
      </div>
    </div>
  );
};

const AARCalculator = () => {
  const [initial, setInitial] = useLocalStorage('aar_initial', 1000);
  const [salvage, setSalvage] = useLocalStorage('aar_salvage', 0);
  const [incomes, setIncomes] = useLocalStorage('aar_incomes', [200, 220, 240, 260]);
  const aar = useMemo(() => {
    const avgIncome = incomes.reduce((s,x)=>s+Number(x),0) / (incomes.length||1);
    const avgInvestment = (Number(initial) + Number(salvage)) / 2 || 1;
    return avgIncome / avgInvestment;
  }, [initial, salvage, incomes]);
  return (
    <div>
      <CardHeader title="Average Accounting Return (AAR)" />
      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        <div className="space-y-4">
          <div><label className="font-semibold">Initial Investment</label><input type="number" value={initial} onChange={(e)=>setInitial(Number(e.target.value))} className="w-full mt-1 p-2 border rounded-lg"/></div>
          <div><label className="font-semibold">Salvage Value</label><input type="number" value={salvage} onChange={(e)=>setSalvage(Number(e.target.value))} className="w-full mt-1 p-2 border rounded-lg"/></div>
          <div>
            <label className="font-semibold">Annual Net Incomes</label>
            <div className="space-y-2 mt-1">
              {incomes.map((v,i)=> (
                <div key={i} className="flex items-center gap-2">
                  <input type="number" value={v} onChange={(e)=>{ const c=[...incomes]; c[i]=Number(e.target.value); setIncomes(c);} } className="flex-1 p-2 border rounded-lg" />
                  <button onClick={()=>setIncomes(incomes.filter((_,idx)=>idx!==i))} className="text-red-600">Remove</button>
                </div>
              ))}
              <button onClick={()=>setIncomes([...incomes, 0])} className="text-blue-600 font-semibold">+ Add Year</button>
            </div>
          </div>
        </div>
        <div className="space-y-4">
          <div className="bg-blue-50 border-2 border-blue-200 rounded-xl p-4"><p className="text-gray-600">AAR</p><p className="text-3xl font-bold text-blue-700">{(aar*100).toFixed(2)}%</p><p className="text-sm text-gray-500">AAR ≈ Average Net Income / Average Investment</p></div>
        </div>
      </div>
    </div>
  );
};

const PICalculator = () => {
  const [initial, setInitial] = useLocalStorage('pi_initial', 1000);
  const [rate, setRate] = useLocalStorage('pi_rate', 10);
  const [flows, setFlows] = useLocalStorage('pi_flows', [300, 300, 300, 300]);
  const pi = useMemo(() => {
    const r = rate/100; let pv = 0;
    flows.forEach((cf, t) => { pv += Number(cf) / Math.pow(1 + r, t + 1); });
    const denom = Number(initial) || 1;
    return pv / denom;
  }, [initial, rate, flows]);
  return (
    <div>
      <CardHeader title="Profitability Index (PI)" />
      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        <div className="space-y-4">
          <div><label className="font-semibold">Initial Investment (CF0)</label><input type="number" value={initial} onChange={(e)=>setInitial(Number(e.target.value))} className="w-full mt-1 p-2 border rounded-lg"/></div>
          <div><label className="font-semibold">Discount Rate (%)</label><input type="number" step="0.1" value={rate} onChange={(e)=>setRate(Number(e.target.value))} className="w-full mt-1 p-2 border rounded-lg"/></div>
          <div>
            <label className="font-semibold">Cash Flows</label>
            <div className="space-y-2 mt-1">
              {flows.map((cf,i)=> (
                <div key={i} className="flex items-center gap-2">
                  <input type="number" value={cf} onChange={(e)=>{ const c=[...flows]; c[i]=Number(e.target.value); setFlows(c);} } className="flex-1 p-2 border rounded-lg" />
                  <button onClick={()=>setFlows(flows.filter((_,idx)=>idx!==i))} className="text-red-600">Remove</button>
                </div>
              ))}
              <button onClick={()=>setFlows([...flows, 0])} className="text-blue-600 font-semibold">+ Add Cash Flow</button>
            </div>
          </div>
        </div>
        <div className="space-y-4">
          <div className="bg-blue-50 border-2 border-blue-200 rounded-xl p-4"><p className="text-gray-600">PI</p><p className="text-3xl font-bold text-blue-700">{pi.toFixed(2)}</p><p className="text-sm text-gray-500">PI = PV of future cash inflows / Initial Investment</p></div>
        </div>
      </div>
    </div>
  );
};

// --- MAIN APP ---
export default function App() {
  const [activePage, setActivePage] = useState('dashboard');
  const [glossaryQuery, setGlossaryQuery] = useState('');
  const { lang, setLang, t } = useI18n('ALB');

  useEffect(() => { logActivity('visit_page', { page: activePage }); }, [activePage]);

  const renderPage = () => {
    switch (activePage) {
      case 'dashboard':
        return <Dashboard setActivePage={setActivePage} t={t} />;
      case 'modules':
        return <LearningModules t={t} />;
      case 'toolkit':
        return <FinancialToolkit t={t} />;
      case 'cases':
        return <CaseStudyZone t={t} />;
      case 'simulation':
        return <StockMarketSimulation t={t} />;
      case 'glossary':
        return <Glossary t={t} query={glossaryQuery} onNavigateModule={() => setActivePage('modules')} />;
      default:
        return <Dashboard setActivePage={setActivePage} t={t} />;
    }
  };

  return (
    <div className="relative min-h-screen font-sans text-gray-800 overflow-x-hidden">
      {/* Animated background layer */}
      <div className="fixed inset-0 -z-10 bg-gradient-animate">
        <div className="bg-blob bg-blob-1" />
        <div className="bg-blob bg-blob-2" />
        <div className="bg-blob bg-blob-3" />
      </div>
      <Header setActivePage={setActivePage} setGlossaryQuery={setGlossaryQuery} lang={lang} setLang={setLang} t={t} />
      <main className="p-4 pb-24 sm:p-6 lg:p-8">
        {renderPage()}
      </main>
    </div>
  );
}

// --- HEADER (Removed user avatar and name) ---
const Header = ({ setActivePage, setGlossaryQuery, lang = 'ALB', setLang, t = (s)=>s }) => {
  const navItems = [
    { id: 'dashboard', label: t('Dashboard'), icon: Home },
    { id: 'modules', label: t('Learning Modules'), icon: BookOpen },
    { id: 'toolkit', label: t('Toolkit'), icon: Calculator },
    { id: 'cases', label: t('Case Studies'), icon: Briefcase },
    { id: 'simulation', label: t('The Simulation'), icon: TrendingUp },
    { id: 'glossary', label: t('Glossary'), icon: Search },
  ];

  return (
    <header className="bg-white shadow-md sticky top-0 z-50">
      <div className="container mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex items-center justify-between h-16">
          <div className="flex items-center space-x-4">
            <Euro className="w-8 h-8 text-blue-600" />
            <span className="text-xl font-bold text-gray-900">Corporate Finance 101</span>
          </div>
          <nav className="hidden md:flex items-center space-x-6">
            {navItems.map(item => (
              <button key={item.id} onClick={() => setActivePage(item.id)} className="flex items-center space-x-2 text-gray-600 hover:text-blue-600 transition-colors duration-200">
                <item.icon className="w-5 h-5" />
                <span>{item.label}</span>
              </button>
            ))}
          </nav>
          <div className="flex items-center space-x-4">
            <button onClick={() => window.scrollTo({ top: 0, behavior: 'smooth' })} className="px-3 py-1 rounded-full border text-sm hover:bg-gray-50">{t('Top')}</button>
            <button
              onClick={() => {
                if (window.confirm('This will clear all saved local data and reload the app. Continue?')) {
                  try { window.localStorage.clear(); } catch (e) {}
                  window.location.reload();
                }
              }}
              className="px-3 py-1 rounded-full border text-sm text-red-700 hover:bg-red-50"
              title="Clear all locally saved app data"
            >
              {t('Reset Data')}
            </button>
            {/* Language Toggle */}
            <div className="flex items-center">
              <button onClick={()=> setLang(lang==='ALB'?'ENG':'ALB')} className="relative inline-flex items-center h-7 px-1 rounded-full border bg-white shadow-sm">
                <span className={`text-xs px-2 py-0.5 rounded-full ${lang==='ALB'?'bg-indigo-600 text-white':'text-gray-600'}`}>ALB</span>
                <span className={`text-xs px-2 py-0.5 rounded-full ${lang==='ENG'?'bg-indigo-600 text-white':'text-gray-600'}`}>ENG</span>
              </button>
            </div>
          </div>
        </div>
      </div>
      {/* Mobile bottom tab bar including Glossary */}
      <div className="md:hidden fixed bottom-0 left-0 right-0 bg-white/95 backdrop-blur border-t z-50 mobile-tab-entrance">
        <div className="px-2 py-1 sm:px-3 flex justify-between">
          {navItems.map(item => (
            <button key={item.id} onClick={() => setActivePage(item.id)} className="flex flex-col items-center gap-0.5 text-gray-600 hover:text-blue-600 transition-colors duration-200 p-2">
              <item.icon className="w-6 h-6" />
              <span className="text-[11px] leading-none">{item.label.split(' ')[0]}</span>
            </button>
          ))}
        </div>
      </div>
    </header>
  );
};

// --- DASHBOARD (Removed welcome hero, added live ticker card and real activity) ---
const Dashboard = ({ setActivePage, t = (s)=>s }) => {
  const [fact, setFact] = useState('');
  useEffect(() => {
    setFact(financeFacts[Math.floor(Math.random() * financeFacts.length)]);
  }, []);

  const [progressMap, setProgressMap] = useLocalStorage('module_progress', modules.reduce((acc,m)=>{acc[m.id]=m.progress;return acc;}, {}));
  const overallProgress = useMemo(() => {
    const vals = Object.values(progressMap);
    const total = vals.reduce((s,v)=>s+v, 0);
    return Math.round(total / vals.length);
  }, [progressMap]);
  const progressData = [{ name: 'Completed', value: overallProgress }, { name: 'Remaining', value: 100 - overallProgress }];
  const COLORS = ['#4A90E2', '#E8E8E8'];

  // Today activity
  const todayActivities = useMemo(() => {
    const list = JSON.parse(localStorage.getItem('activity_log') || '[]');
    const today = new Date().toISOString().slice(0,10);
    return list.filter(x => (x.ts||'').slice(0,10) === today).slice(0,10);
  }, []);

  return (
    <div className="space-y-8 ios-safe snap-container">
      {/* Educational Credit Banner */}
      <div className="bg-yellow-50 border border-yellow-200 text-yellow-900 rounded-lg p-3 text-center snap-card">
        {t('Built for educational purposes by')} <a href="https://www.arberhoti.com" target="_blank" rel="noreferrer" className="underline font-semibold">Prof.asoc.dr. Arber HOTI, AC, KC, ABC.</a>
      </div>
      {/* Live Market Ticker */}
      <div className="snap-card"><TickerBar t={t} /></div>

      {/* Main content (progress card removed as requested) */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
        <div className="lg:col-span-2 space-y-8">
          <Card className="snap-card">
            <CardHeader title={t('Market Insights')} />
            <ul className="list-disc ml-5 text-gray-700 space-y-2">
              <li>{t('Live quotes on this dashboard are powered by TwelveData and refresh hourly or on load.')}</li>
              <li>{t('Big intraday swings often coincide with earnings, guidance updates, or macro data releases.')}</li>
              <li>{t('Diversify across sectors to reduce unsystematic risk; monitor position sizes versus risk tolerance.')}</li>
            </ul>
          </Card>
          

          <Card className="snap-card">
            <CardHeader title={t('Quick Tips')} />
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-3 text-sm text-gray-700">
              <div className="p-3 rounded-lg bg-gray-50">{t('Use the Toolkit to compute NPV/IRR before accepting a project.')}</div>
              <div className="p-3 rounded-lg bg-gray-50">{t('Estimate your firm’s WACC for a consistent discount rate.')}</div>
              <div className="p-3 rounded-lg bg-gray-50">{t('In the Simulator, practice position sizing and diversification.')}</div>
            </div>
          </Card>

          <Card className="snap-card">
            <CardHeader title={t('Your Learning Path')} />
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
              <button onClick={() => setActivePage('modules')} className="group p-4 rounded-xl border bg-white shadow-sm hover:shadow-md transition-all hover-pop">
                <div className="h-24 rounded-lg mb-3" style={{background: 'linear-gradient(135deg,#E0F2FE,#FEF9C3)'}} />
                <div className="flex items-center gap-2">
                  <BookOpen className="w-5 h-5 text-blue-600"/>
                  <span className="font-semibold">{t('Continue Modules')}</span>
                </div>
                <p className="text-sm text-gray-600 mt-1">{t('Pick up where you left off in the curriculum.')}</p>
              </button>
              <button onClick={() => setActivePage('toolkit')} className="group p-4 rounded-xl border bg-white shadow-sm hover:shadow-md transition-all hover-pop">
                <div className="h-24 rounded-lg mb-3" style={{background: 'linear-gradient(135deg,#EEF2FF,#E9D5FF)'}} />
                <div className="flex items-center gap-2">
                  <Calculator className="w-5 h-5 text-indigo-600"/>
                  <span className="font-semibold">{t('Open Toolkit')}</span>
                </div>
                <p className="text-sm text-gray-600 mt-1">{t('Use NPV/IRR, WACC, DDM, and more.')}</p>
              </button>
              <button onClick={() => setActivePage('simulation')} className="group p-4 rounded-xl border bg-white shadow-sm hover:shadow-md transition-all hover-pop">
                <div className="h-24 rounded-lg mb-3" style={{background: 'linear-gradient(135deg,#DCFCE7,#E0E7FF)'}} />
                <div className="flex items-center gap-2">
                  <TrendingUp className="w-5 h-5 text-green-600"/>
                  <span className="font-semibold">{t('Go to Simulator')}</span>
                </div>
                <p className="text-sm text-gray-600 mt-1">{t('Practice trading with live market data.')}</p>
              </button>
              <button onClick={() => setActivePage('cases')} className="group p-4 rounded-xl border bg-white shadow-sm hover:shadow-md transition-all hover-pop">
                <div className="h-24 rounded-lg mb-3" style={{background: 'linear-gradient(135deg,#FFE4E6,#FDE68A)'}} />
                <div className="flex items-center gap-2">
                  <Briefcase className="w-5 h-5 text-rose-600"/>
                  <span className="font-semibold">{t('Try Case Studies')}</span>
                </div>
                <p className="text-sm text-gray-600 mt-1">{t('Work through guided corporate finance cases.')}</p>
              </button>
            </div>
          </Card>
        </div>

        {/* Right Column: Fact of the day */}
        <div className="space-y-8">
          <Card className="snap-card">
            <CardHeader title={t('Fact of the Day')} icon={Star} />
            <p className="text-gray-600 italic">"{t(fact)}"</p>
          </Card>
        </div>
      </div>
    </div>
  );
};

// --- LEARNING MODULES & TOOLKIT ---
const LearningModules = ({ t = (s)=>s }) => {
  const [selectedModule, setSelectedModule] = useState(modules[0]);
  useEffect(() => { logActivity('open_module', { id: selectedModule.id, title: selectedModule.title }); }, [selectedModule]);

  return (
    <div className="fade-in">
      <h1 className="text-3xl font-bold mb-6">{t('Interactive Learning Modules')}</h1>
      <div className="grid grid-cols-1 md:grid-cols-4 gap-6">
        <div className="md:col-span-1">
          <Card>
            <ul className="space-y-2">
              {modules.map(mod => (
                <li key={mod.id}>
                  <button onClick={() => setSelectedModule(mod)} className={`w-full text-left p-3 rounded-lg flex items-center space-x-3 transition-colors ${selectedModule.id === mod.id ? 'bg-blue-100 text-blue-700 font-semibold' : 'hover:bg-gray-100'}`}>
                    <div className="p-2 rounded-full" style={{backgroundColor: `${mod.color}20`}}>
                      <mod.icon className="w-5 h-5" style={{color: mod.color}} />
                    </div>
                    <span>{t(mod.title)}</span>
                  </button>
                </li>
              ))}
            </ul>
          </Card>
        </div>
        <div className="md:col-span-3">
          <Card>
            <CardHeader title={t(selectedModule.title)} />
            {selectedModule.id === 1 ? (
              <div className="space-y-8">
                <TimeValueMoneyInteractive />
                <QuizTVM />
              </div>
            ) : selectedModule.id === 2 ? (
              <div className="space-y-8">
                <CapitalBudgetingInteractive />
                <QuizCapitalBudgeting />
                <NewsSnippetCapitalBudgeting />
              </div>
            ) : selectedModule.id === 3 ? (
              <div className="space-y-8">
                <RiskReturnInteractive />
              </div>
            ) : selectedModule.id === 4 ? (
              <div className="space-y-8">
                <CostOfCapitalInteractive />
              </div>
            ) : selectedModule.id === 5 ? (
              <div className="space-y-8">
                <CapitalStructureInteractive />
              </div>
            ) : (
              <ModulePlaceholder title={selectedModule.title} />
            )}
          </Card>
        </div>
      </div>
    </div>
  );
};

const FinancialToolkit = ({ t = (s)=>s }) => {
  const [activeTool, setActiveTool] = useLocalStorage('activeTool', 'NPV & IRR');
  const tools = ['NPV & IRR', 'WACC', 'Loan Amortization', 'Bond Valuation', 'DDM (Dividend Discount)', 'Beta (CAPM)', 'Payback', 'Discounted Payback', 'AAR', 'PI'];

  return (
    <div className="fade-in">
      <h1 className="text-3xl font-bold mb-2">{t('Financial Toolkit')}</h1>
      <p className="text-gray-600 mb-6 max-w-2xl">{t('Interactive calculators with formulas and tips. All computations run locally in your browser.')}</p>
      <div className="grid grid-cols-1 lg:grid-cols-4 gap-6">
        <div className="lg:col-span-1">
          <Card>
            <div className="space-y-2">
              {tools.map(toolKey => (
                <button key={toolKey} onClick={() => setActiveTool(toolKey)} className={`w-full text-left px-4 py-2 rounded-lg ${activeTool === toolKey ? 'bg-blue-100 text-blue-700 font-semibold' : 'hover:bg-gray-100'}`}>{t(toolKey)}</button>
              ))}
            </div>
          </Card>
        </div>
        <div className="lg:col-span-3">
          <Card>
            {activeTool === 'NPV & IRR' && <NPVIRRCalculator t={t} />}
            {activeTool === 'WACC' && <WACCCalculator t={t} />}
            {activeTool === 'Loan Amortization' && <LoanAmortization t={t} />}
            {activeTool === 'Bond Valuation' && <BondValuation t={t} />}
            {activeTool === 'DDM (Dividend Discount)' && <DDMCalculator t={t} />}
            {activeTool === 'Beta (CAPM)' && <BetaCalculator t={t} />}
            {activeTool === 'Payback' && <PaybackCalculator t={t} />}
            {activeTool === 'Discounted Payback' && <DiscountedPaybackCalculator t={t} />}
            {activeTool === 'AAR' && <AARCalculator t={t} />}
            {activeTool === 'PI' && <PICalculator t={t} />}
          </Card>
        </div>
      </div>
    </div>
  );
};

// --- CASES & SIMULATOR (log trades to activity) ---
const CaseStudyZone = ({ t = (s)=>s }) => <DealRoomFlow t={t} />;

const DealRoomFlow = ({ t = (s)=>s }) => {
  const [step, setStep] = useLocalStorage('deal_step', 1);
  const [recommendation, setRecommendation] = useLocalStorage('deal_reco', '');
  const [caseId, setCaseId] = useLocalStorage('case_id', 'alpha');

  const caseStudies = {
    alpha: {
      name: 'Alpha Widgets Expansion',
      income_statement: [ { year: 1, revenue: 1200, cogs: 600, opex: 300, dep: 50 }, { year: 2, revenue: 1350, cogs: 675, opex: 320, dep: 55 } ],
      balance_sheet: [ { year: 1, cash: 200, debt: 400, equity: 600 }, { year: 2, cash: 260, debt: 380, equity: 670 } ],
      cash_flows: [ { year: 1, operating: 180, investing: -120, financing: -20 }, { year: 2, operating: 210, investing: -140, financing: -30 } ],
      project_flows: [ -1000, 300, 320, 340, 360, 380 ],
    },
    beta: {
      name: 'Beta Foods New Plant',
      income_statement: [ { year: 1, revenue: 2200, cogs: 1210, opex: 600, dep: 120 }, { year: 2, revenue: 2500, cogs: 1350, opex: 650, dep: 130 } ],
      balance_sheet: [ { year: 1, cash: 350, debt: 900, equity: 800 }, { year: 2, cash: 420, debt: 880, equity: 920 } ],
      cash_flows: [ { year: 1, operating: 260, investing: -400, financing: 100 }, { year: 2, operating: 320, investing: -250, financing: -50 } ],
      project_flows: [ -2000, 500, 550, 600, 650, 700 ],
    },
    gamma: {
      name: 'Gamma Tech Product Launch',
      income_statement: [ { year: 1, revenue: 1800, cogs: 720, opex: 700, dep: 80 }, { year: 2, revenue: 2300, cogs: 900, opex: 820, dep: 90 } ],
      balance_sheet: [ { year: 1, cash: 150, debt: 200, equity: 850 }, { year: 2, cash: 260, debt: 180, equity: 990 } ],
      cash_flows: [ { year: 1, operating: 140, investing: -300, financing: 50 }, { year: 2, operating: 260, investing: -150, financing: -20 } ],
      project_flows: [ -1500, 350, 420, 480, 520, 560 ],
    }
  };

  const current = caseStudies[caseId];

  useEffect(() => { logActivity('dealroom_step', { step, case: caseId }); }, [step, caseId]);

  const Table = ({ rows, cols }) => (
    <div className="overflow-auto">
      <table className="w-full text-left">
        <thead>
          <tr className="text-gray-500 border-b">
            {cols.map(c => <th key={c} className="py-2 pr-4">{t(c)}</th>)}
          </tr>
        </thead>
        <tbody>
          {rows.map((r, i) => (
            <tr key={i} className="border-b last:border-0">
              {cols.map(c => <td key={c} className="py-2 pr-4">{r[c.toLowerCase()] ?? r[c] ?? ''}</td>)}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );

  return (
    <div className="fade-in">
      <h1 className="text-3xl font-bold mb-6">{t('The "Deal Room" - Guided Case Study')}</h1>
      <div className="mb-4 flex items-center gap-3">
        <label className="font-semibold">{t('Case Study:')}</label>
        <select value={caseId} onChange={(e)=>setCaseId(e.target.value)} className="border p-2 rounded">
          <option value="alpha">{caseStudies.alpha.name}</option>
          <option value="beta">{caseStudies.beta.name}</option>
          <option value="gamma">{caseStudies.gamma.name}</option>
        </select>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        <div className="lg:col-span-2 space-y-6">
          {step === 1 && (
            <Card>
              <CardHeader title={t('Step 1: Review Statements (Inline)')} />
              <h3 className="font-semibold mb-2">{t('Income Statement')}</h3>
              <Table cols={["Year","Revenue","COGS","Opex","Dep"]} rows={current.income_statement.map(x=>({Year:x.year, Revenue:x.revenue, COGS:x.cogs, Opex:x.opex, Dep:x.dep}))} />
              <h3 className="font-semibold my-2">{t('Balance Sheet')}</h3>
              <Table cols={["Year","Cash","Debt","Equity"]} rows={current.balance_sheet.map(x=>({Year:x.year, Cash:x.cash, Debt:x.debt, Equity:x.equity}))} />
              <h3 className="font-semibold my-2">{t('Cash Flow Statement')}</h3>
              <Table cols={["Year","Operating","Investing","Financing"]} rows={current.cash_flows.map(x=>({Year:x.year, Operating:x.operating, Investing:x.investing, Financing:x.financing}))} />
              <div className="mt-4">
                <button onClick={() => setStep(2)} className="bg-blue-600 text-white px-4 py-2 rounded-lg">{t('Continue to Analysis')}</button>
              </div>
            </Card>
          )}

          {step === 2 && (
            <Card>
              <CardHeader title={t('Step 2: Perform Analysis')} />
              <p className="text-gray-600">{t('Open the Toolkit and use NPV & IRR (or WACC if needed). Assume discount rate 10% unless you compute one.')}</p>
              <ul className="list-disc ml-5 text-gray-600 mt-2">
                <li>{t('Compute NPV using project flows:')} {JSON.stringify(current.project_flows)}</li>
                <li>{t('Estimate IRR; note any multiple IRR issues if signs change.')}</li>
                <li>{t('Consider qualitative factors: strategy, risk, and resources.')}</li>
              </ul>
              <div className="mt-4">
                <button onClick={() => setStep(3)} className="bg-blue-600 text-white px-4 py-2 rounded-lg">{t('Proceed to Recommendation')}</button>
              </div>
            </Card>
          )}

          {step === 3 && (
            <Card>
              <CardHeader title={t('Step 3: Submit Recommendation')} />
              <label className="font-semibold">{t('Your Recommendation')}</label>
              <textarea value={recommendation} onChange={(e)=> setRecommendation(e.target.value)} rows={5} className="w-full mt-2 p-3 border rounded-lg" placeholder={t('State your decision (Accept/Reject) and justify with NPV/IRR and qualitative factors.')} />
              <div className="mt-3 flex items-center gap-3">
                <button onClick={() => { logActivity('dealroom_submit', { case: caseId }); setStep(4); }} disabled={!recommendation.trim()} className="bg-green-600 text-white px-4 py-2 rounded-lg disabled:opacity-50">{t('Submit')}</button>
                <button onClick={() => setRecommendation('')} className="px-4 py-2 rounded-lg border">{t('Clear')}</button>
              </div>
            </Card>
          )}

          {step === 4 && (
            <Card>
              <CardHeader title={t('Step 4: Solution Walkthrough')} />
              <p className="text-gray-700 mb-3">{t('An acceptable solution could be:')}</p>
              <ul className="list-disc ml-5 text-gray-600">
                <li>{t('NPV at 10% ≈ positive (based on sample flows), hence value creation.')}</li>
                <li>{t('IRR ≈ between 12%–14% (illustrative); accept if above cost of capital.')}</li>
                <li>{t('Qualitative: aligns with product roadmap; moderate execution risk; resource availability confirmed.')}</li>
              </ul>
              <div className="mt-4 flex items-center gap-3">
                <button onClick={() => setStep(1)} className="px-4 py-2 rounded-lg border">{t('Restart Case')}</button>
                <button onClick={() => setStep(3)} className="px-4 py-2 rounded-lg border">{t('Edit Recommendation')}</button>
              </div>
            </Card>
          )}
        </div>

        <div className="space-y-6">
          <Card>
            <CardHeader title={t('Progress')} />
            <p className="text-gray-600">{t('Step of 4').replace('{step}', step)}</p>
            <div className="w-full bg-gray-100 h-2 rounded mt-2"><div className="bg-blue-600 h-2 rounded" style={{width: `${(step/4)*100}%`}} /></div>
          </Card>
          <Card>
            <CardHeader title={t('Tips')} />
            <ul className="list-disc ml-5 text-gray-600">
              <li>{t('Use the Toolkit to ensure consistency of assumptions.')}</li>
              <li>{t('Cross-check cash flow timing and signs.')}</li>
              <li>{t('Document key assumptions in your write-up.')}</li>
            </ul>
          </Card>
        </div>
      </div>
    </div>
  );
};

const StockMarketSimulation = ({ t = (s)=>s }) => {
  const apiKey = getApiKey();
  const [cash, setCash] = useLocalStorage('ex_cash', 100000);
  const [holdings, setHoldings] = useLocalStorage('ex_holdings', []); // [{symbol, qty, avgCost}]
  const [symbol, setSymbol] = useState('AAPL');
  const [qty, setQty] = useState(10);
  const [achievements, setAchievements] = useLocalStorage('ex_achievements', { firstTrade: false, diversified: false, sixFigure: false });
  const [usSymbols, setUsSymbols] = useLocalStorage('us_symbols', []);
  // Seed with last-known-good quotes from dashboard ticker cache for instant UI
  const [quotes, setQuotes] = useState(() => {
    try {
      return (JSON.parse(localStorage.getItem('ticker_cache_v2') || '{}').quotes) || {};
    } catch { return {}; }
  }); // {SYM: quote}

  // Fetch symbols once if not present
  useEffect(() => {
    let cancelled = false;
    async function fetchSymbols() {
      try {
        if (usSymbols && usSymbols.length) return;
        const r = await fetch(`https://finnhub.io/api/v1/stock/symbol?exchange=US&token=${apiKey}`);
        const data = await r.json();
        if (!cancelled && Array.isArray(data)) {
          const list = data.map(d => d.symbol).filter(Boolean);
          setUsSymbols(list);
        }
      } catch {}
    }
    fetchSymbols();
    return () => { cancelled = true; };
  }, [apiKey, usSymbols, setUsSymbols]);

  // Fetch quotes for current selection and holdings via Netlify Function (batched)
  useEffect(() => {
    let cancelled = false;
    async function load() {
      const need = Array.from(new Set([symbol, ...holdings.map(h=>h.symbol)]));
      if (!need.length) return;
      try {
        const controller = new AbortController();
        const t = setTimeout(() => controller.abort(), 7000);
        const url = `/.netlify/functions/quotes?symbols=${encodeURIComponent(need.join(','))}`;
        const res = await fetch(url, { signal: controller.signal });
        const json = await res.json().catch(()=>({}));
        clearTimeout(t);
        if (cancelled) return;
        const next = json && json.quotes ? json.quotes : {};
        setQuotes(prev => ({ ...prev, ...next }));
      } catch {}
    }
    load();
    const id = setInterval(load, 3600000); // refresh hourly to match policy
    return () => { cancelled = true; clearInterval(id); };
  }, [apiKey, symbol, holdings]);

  const priceOf = (sym) => {
    const p = quotes[sym]?.c;
    return typeof p === 'number' && !Number.isNaN(p) ? p : 0;
  };

  const portfolioValue = useMemo(() => holdings.reduce((sum, h) => sum + priceOf(h.symbol) * h.qty, 0), [holdings, quotes]);
  const totalValue = useMemo(() => cash + portfolioValue, [cash, portfolioValue]);

  const onTrade = (side) => {
    const price = priceOf(symbol);
    const quantity = Number(qty);
    if (!price || quantity <= 0) return;
    if (side === 'BUY') {
      const cost = price * quantity; if (cost > cash) return;
      const idx = holdings.findIndex(h => h.symbol === symbol); const newHoldings = [...holdings];
      if (idx >= 0) { const h = newHoldings[idx]; const newQty = h.qty + quantity; const newAvg = (h.avgCost * h.qty + price * quantity) / newQty; newHoldings[idx] = { ...h, qty: newQty, avgCost: newAvg }; }
      else { newHoldings.push({ symbol, qty: quantity, avgCost: price }); }
      setHoldings(newHoldings); setCash(cash - cost); logActivity('trade_buy', { symbol, qty: quantity, price });
    } else {
      const idx = holdings.findIndex(h => h.symbol === symbol); if (idx < 0) return;
      const h = holdings[idx]; const sellQty = Math.min(h.qty, quantity); const proceeds = price * sellQty;
      const newHoldings = [...holdings]; const newQty = h.qty - sellQty; if (newQty === 0) newHoldings.splice(idx, 1); else newHoldings[idx] = { ...h, qty: newQty };
      setHoldings(newHoldings); setCash(cash + proceeds); logActivity('trade_sell', { symbol, qty: sellQty, price });
    }
    setAchievements(prev => ({ firstTrade: true, diversified: prev.diversified || new Set(holdings.map(h => h.symbol).concat([symbol])).size >= 3, sixFigure: prev.sixFigure || (cash + portfolioValue) >= 100500 }));
  };

  // Allocation by symbol (dollars per symbol; chart computes %)
  const allocationData = useMemo(() => {
    const totalsBySym = {};
    holdings.forEach(h => {
      const px = priceOf(h.symbol) || h.avgCost || 0;
      const val = Number(px) * Number(h.qty || 0);
      if (val > 0) totalsBySym[h.symbol] = (totalsBySym[h.symbol] || 0) + val;
    });
    return Object.entries(totalsBySym)
      .map(([name, dollars]) => ({ name, value: Number(dollars) }))
      .filter(e => isFinite(e.value) && e.value > 0);
  }, [holdings, quotes]);

  return (
    <div className="fade-in">
      <h1 className="text-3xl font-bold mb-6">{t('SimulationTitle')}</h1>
      <p className="text-sm text-green-700 mb-2">Note: Quotes and prices below use LIVE market data via Finnhub (updated periodically).</p>
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        <div className="lg:col-span-2 space-y-6">
          <Card>
            <CardHeader title={t('Your Portfolio')}/>
            <div className="mb-4 flex flex-col sm:flex-row items-stretch sm:items-center gap-3 sim-form-entrance" role="form" aria-label="Trade form">
              <div className="flex items-center gap-2 w-full sm:w-auto">
                <input list="symbols-list" value={symbol} onChange={(e)=>setSymbol(e.target.value.toUpperCase())} placeholder="Enter ticker (e.g., AAPL)" className="border p-3 rounded-lg w-full sm:w-44" aria-label="Ticker" />
                <datalist id="symbols-list">
                  {(usSymbols.length ? usSymbols.slice(0,1000) : ['AAPL','MSFT','TSLA','GOOGL','AMZN']).map(s => <option key={s} value={s} />)}
                </datalist>
                <span className="text-sm text-gray-500 whitespace-nowrap">{t('Price')}: {priceOf(symbol)? '$'+priceOf(symbol).toFixed(2) : '—'}</span>
              </div>
              <input type="number" inputMode="numeric" value={qty} onChange={(e) => setQty(Number(e.target.value))} className="border p-3 rounded-lg w-full sm:w-24" aria-label={t('Quantity')} />
              <div className="flex gap-2 w-full sm:w-auto">
                <button onClick={() => onTrade('BUY')} className="flex-1 sm:flex-none bg-green-600 text-white px-5 py-3 rounded-lg hover:bg-green-700">{t('Buy')}</button>
                <button onClick={() => onTrade('SELL')} className="flex-1 sm:flex-none bg-red-600 text-white px-5 py-3 rounded-lg hover:bg-red-700">{t('Sell')}</button>
              </div>
              <div className="sm:ml-auto text-sm text-gray-600">{t('Cash')}: ${cash.toFixed(2)} | {t('Value')}: ${totalValue.toFixed(2)}</div>
            </div>
            <div className="overflow-auto rounded-xl shadow ring-1 ring-black/5">
              <table className="w-full text-sm" aria-label="Holdings table">
                <thead>
                  <tr className="bg-gradient-to-r from-blue-600 to-indigo-600 text-white">
                    <th className="py-3 px-3 text-left">{t('Symbol')}</th>
                    <th className="py-3 px-3 text-right">{t('Qty')}</th>
                    <th className="py-3 px-3 text-right">{t('Avg Cost')}</th>
                    <th className="py-3 px-3 text-right">{t('Price')}</th>
                    <th className="py-3 px-3 text-right">{t('P/L')}</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-100 bg-white">
                  {holdings.length === 0 && (
                    <tr><td colSpan="5" className="py-6 px-3 text-center text-gray-500">{t('No holdings. Make your first trade!')}</td></tr>
                  )}
                  {holdings.map(h => {
                    const price = priceOf(h.symbol); const pl = (price - h.avgCost) * h.qty; const plUp = pl >= 0;
                    return (
                      <tr key={h.symbol} className="group hover:bg-gradient-to-r hover:from-blue-50 hover:to-indigo-50 transition-colors">
                        <td className="py-3 px-3 font-semibold">
                          <span className="inline-flex items-center gap-2">
                            <span className="h-2.5 w-2.5 rounded-full bg-indigo-400 group-hover:scale-110 transition-transform"></span>
                            {h.symbol}
                          </span>
                        </td>
                        <td className="py-3 px-3 text-right tabular-nums">{h.qty}</td>
                        <td className="py-3 px-3 text-right tabular-nums">{'$'}{h.avgCost.toFixed(2)}</td>
                        <td className="py-3 px-3 text-right tabular-nums">{price? '$'+price.toFixed(2) : '—'}</td>
                        <td className={`py-3 px-3 text-right font-semibold tabular-nums ${plUp ? 'text-green-600' : 'text-red-600'}`}>
                          <span className={`inline-flex items-center gap-1 px-2 py-1 rounded-full text-xs ${plUp ? 'bg-green-50 ring-1 ring-green-200' : 'bg-red-50 ring-1 ring-red-200'}`}>
                            <span className={`h-1.5 w-1.5 rounded-full ${plUp ? 'bg-green-500' : 'bg-red-500'}`}></span>
                            {plUp ? '+' : ''}{'$'}{pl.toFixed(2)}
                          </span>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          </Card>

          <Card>
            <CardHeader title={t('Allocation by Symbol (%)')} />
            {allocationData.length === 0 ? (
              <div className="p-4 text-sm text-gray-600">{t('No positions to chart yet. Buy at least one symbol to see your allocation.')}</div>
            ) : (
              <div style={{ width: '100%', height: 300 }} role="img" aria-label="Diversification chart">
                <ResponsiveContainer>
                  <PieChart>
                    <Pie
                      data={allocationData}
                      dataKey="value"
                      nameKey="name"
                      cx="50%"
                      cy="50%"
                      outerRadius={100}
                      label={(e) => `${e.name}: ${(e.percent * 100).toFixed(1)}%`}
                    >
                      {allocationData.map((_, i) => (
                        <Cell key={i} fill={["#6366F1","#10B981","#F59E0B","#EC4899","#22D3EE","#8B5CF6"][i % 6]} />
                      ))}
                    </Pie>
                    <Tooltip formatter={(val, name, p) => [`$${Number(val).toFixed(2)}`, p && p.payload ? p.payload.name : name]} />
                    <Legend verticalAlign="bottom" height={36} />
                  </PieChart>
                </ResponsiveContainer>
              </div>
            )}
          </Card>
        </div>

        <div className="space-y-6">
          <Card>
            <CardHeader title={t('Achievements')} />
            <ul className="space-y-2">
              <li className="flex items-center gap-2"><Award className={`w-4 h-4 ${achievements.firstTrade ? 'text-yellow-500' : 'text-gray-300'}`} /> {t('First Trade')}</li>
              <li className="flex items-center gap-2"><Star className={`w-4 h-4 ${achievements.diversified ? 'text-blue-500' : 'text-gray-300'}`} /> {t('3+ Symbols Diversified')}</li>
              <li className="flex items-center gap-2"><TrendingUp className={`w-4 h-4 ${achievements.sixFigure ? 'text-green-600' : 'text-gray-300'}`} /> {t('Portfolio ≥ $100,500')}</li>
            </ul>
          </Card>
        </div>
      </div>
    </div>
  );
};

// --- GLOSSARY ---
const Glossary = ({ query = '', onNavigateModule, t = (s)=>s }) => {
  const [searchTermLS, setSearchTermLS] = useLocalStorage('glossarySearch', '');
  const [searchTerm, setSearchTerm] = useState(query || searchTermLS);
  useEffect(() => { if (query) setSearchTerm(query); }, [query]);
  useEffect(() => { setSearchTermLS(searchTerm); }, [searchTerm, setSearchTermLS]);

  const filtered = useMemo(() => {
    const q = searchTerm.trim().toLowerCase();
    if (!q) return glossaryTerms;
    return glossaryTerms.filter(g => g.term.toLowerCase().includes(q) || g.definition.toLowerCase().includes(q));
  }, [searchTerm]);

  const [selected, setSelected] = useState(filtered[0] || null);
  useEffect(() => { setSelected(filtered[0] || null); }, [filtered]);

  const moduleMap = { 'Time Value of Money': 1 };

  return (
    <div className="fade-in">
      <h1 className="text-3xl font-bold mb-6">{t('Glossary')}</h1>
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        <div className="lg:col-span-1">
          <Card>
            <div className="relative mb-4">
              <input value={searchTerm} onChange={(e) => setSearchTerm(e.target.value)} type="text" placeholder={t('Search terms or definitions')} className="w-full pl-12 pr-4 py-3 border-2 border-gray-200 rounded-full focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent" />
              <Search className="absolute left-4 top-1/2 -translate-y-1/2 w-6 h-6 text-gray-400" />
            </div>
            <ul className="space-y-2 max-h-[480px] overflow-auto pr-2">
              {filtered.map(g => (
                <li key={g.term}>
                  <button onClick={() => setSelected(g)} className={`w-full text-left p-3 rounded-lg ${selected?.term === g.term ? 'bg-blue-100 text-blue-700 font-semibold' : 'hover:bg-gray-100'}`}>{g.term}</button>
                </li>
              ))}
            </ul>
          </Card>
        </div>
        <div className="lg:col-span-2">
          <Card>
            {selected ? (
              <div>
                <CardHeader title={selected.term} />
                <p className="text-gray-700 mb-3"><span className="font-semibold">{t('Definition:')}</span> {selected.definition}</p>
                {selected.formula && <p className="text-gray-700 mb-3"><span className="font-semibold">{t('Formula:')}</span> <span className="font-mono">{selected.formula}</span></p>}
                {selected.example && <p className="text-gray-700 mb-6"><span className="font-semibold">{t('Example:')}</span> {selected.example}</p>}
                {moduleMap[selected.term] && (
                  <button onClick={() => onNavigateModule && onNavigateModule()} className="bg-blue-600 text-white font-semibold px-4 py-2 rounded-lg hover:bg-blue-700">{t('Go to related module')}</button>
                )}
              </div>
            ) : (
              <p className="text-gray-500">{t('No term selected.')}</p>
            )}
          </Card>
        </div>
      </div>
    </div>
  );
};

// --- INTERACTIVE COMPONENTS (from previous implementation) ---
const TimeValueMoneyInteractive = () => {
  const [principal, setPrincipal] = useState(1000);
  const [rate, setRate] = useState(5);
  const [years, setYears] = useState(10);
  const [compounding, setCompounding] = useState(1);

  const { futureValue, chartData } = useMemo(() => {
    const data = []; const n = compounding; const r = rate / 100;
    for (let i = 0; i <= years; i++) {
      const currentFv = principal * Math.pow((1 + r/n), (n*i));
      data.push({ year: i, value: parseFloat(currentFv.toFixed(2)) });
    }
    return { futureValue: data[data.length-1].value, chartData: data };
  }, [principal, rate, years, compounding]);

  return (
    <div className="space-y-6 p-4">
      <p className="text-gray-600">The time value of money (TVM) is the concept that a sum of money is worth more now than the same sum will be at a future date due to its earning potential in the interim.</p>
      <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
        <div className="space-y-6">
          <div>
            <label className="font-semibold">Principal (P): ${principal.toLocaleString()}</label>
            <input type="range" min="100" max="10000" step="100" value={principal} onChange={(e) => setPrincipal(Number(e.target.value))} className="w-full h-2 bg-gray-200 rounded-lg appearance-none cursor-pointer accent-blue-600" />
          </div>
          <div>
            <label className="font-semibold">Annual Interest Rate (r): {rate}%</label>
            <input type="range" min="1" max="20" step="0.5" value={rate} onChange={(e) => setRate(Number(e.target.value))} className="w-full h-2 bg-gray-200 rounded-lg appearance-none cursor-pointer accent-blue-600" />
          </div>
          <div>
            <label className="font-semibold">Years (t): {years}</label>
            <input type="range" min="1" max="50" step="1" value={years} onChange={(e) => setYears(Number(e.target.value))} className="w-full h-2 bg-gray-200 rounded-lg appearance-none cursor-pointer accent-blue-600" />
          </div>
          <div>
            <label className="font-semibold">Compounding Frequency</label>
            <div className="flex items-center space-x-2 mt-2">
              <button onClick={() => setCompounding(1)} className={`px-4 py-2 rounded-full text-sm ${compounding === 1 ? 'bg-blue-600 text-white' : 'bg-gray-200'}`}>Annually</button>
              <button onClick={() => setCompounding(12)} className={`px-4 py-2 rounded-full text-sm ${compounding === 12 ? 'bg-blue-600 text-white' : 'bg-gray-200'}`}>Monthly</button>
            </div>
          </div>
        </div>
        <div className="bg-blue-50 border-2 border-blue-200 rounded-xl p-6 flex flex-col items-center justify-center text-center">
          <p className="text-lg text-gray-600">Future Value (FV)</p>
          <p className="text-5xl font-bold text-blue-700 my-2">${futureValue.toLocaleString('en-US', {minimumFractionDigits: 2, maximumFractionDigits: 2})}</p>
          <p className="text-sm text-gray-500">Formula: FV = P(1 + r/n)^(nt)</p>
        </div>
      </div>
      <div className="mt-8">
        <h3 className="font-semibold mb-4 text-lg">Investment Growth Over Time</h3>
        <div style={{width: '100%', height: 300}}>
          <ResponsiveContainer>
            <LineChart data={chartData} margin={{ top: 5, right: 20, left: 30, bottom: 5 }}>
              <CartesianGrid strokeDasharray="3 3" />
              <XAxis dataKey="year" label={{ value: 'Years', position: 'insideBottom', offset: -5 }}/>
              <YAxis label={{ value: 'Value ($)', angle: -90, position: 'insideLeft' }} tickFormatter={(value) => `$${value.toLocaleString()}`} />
              <Tooltip formatter={(value) => [`$${value.toLocaleString()}`, 'Future Value']}/>
              <Legend />
              <Line type="monotone" dataKey="value" stroke="#4A90E2" strokeWidth={2} dot={false} />
            </LineChart>
          </ResponsiveContainer>
        </div>
      </div>
    </div>
  );
};

const QuizTVM = () => {
  const questions = [
    { q: 'If you invest $1,000 at 10% annually, approximately how much will you have after 2 years (annual compounding)?', options: ['$1,100', '$1,200', '$1,210', '$1,300'], answer: 2, explain: 'FV = 1000*(1+0.10)^2 = 1000*1.21 = 1210.' },
    { q: 'Which factor increases future value the most, all else equal?', options: ['Lower interest rate', 'Higher interest rate', 'Shorter time period', 'No compounding'], answer: 1, explain: 'A higher interest rate increases growth per period.' },
    { q: 'Monthly compounding compared to annual compounding (same APR) leads to:', options: ['Lower future value', 'Same future value', 'Higher future value', 'Cannot be determined'], answer: 2, explain: 'More frequent compounding yields a slightly higher effective rate.' }
  ];
  const [responses, setResponses] = useLocalStorage('tvmQuizResponses', Array(questions.length).fill(null));
  const [submitted, setSubmitted] = useLocalStorage('tvmQuizSubmitted', false);
  const score = useMemo(() => questions.reduce((s, q, i) => s + (responses[i] === q.answer ? 1 : 0), 0), [responses]);
  useEffect(() => { if (submitted) window.localStorage.setItem('tvmQuizScore', String(score)); }, [submitted, score]);

  const submit = () => { setSubmitted(true); logActivity('quiz_submit', { quiz: 'TVM', score }); };

  return (
    <div className="bg-white rounded-xl border p-4">
      <h3 className="text-xl font-bold mb-4">Check Your Understanding: TVM</h3>
      <div className="space-y-6">
        {questions.map((item, idx) => (
          <div key={idx} className="p-3 rounded-lg border">
            <p className="font-medium mb-2">{idx + 1}. {item.q}</p>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
              {item.options.map((opt, oi) => {
                const chosen = responses[idx] === oi;
                const correct = submitted && oi === item.answer;
                const incorrect = submitted && chosen && oi !== item.answer;
                return (
                  <button key={oi} onClick={() => !submitted && setResponses(prev => { const copy = [...prev]; copy[idx] = oi; return copy; })} className={`text-left px-3 py-2 rounded-lg border ${chosen ? 'border-blue-500' : 'border-gray-200'} ${correct ? 'bg-green-50 border-green-500' : ''} ${incorrect ? 'bg-red-50 border-red-500' : ''}`}>{opt}</button>
                );
              })}
            </div>
            {submitted && (
              <p className={`mt-2 text-sm ${responses[idx] === item.answer ? 'text-green-700' : 'text-red-700'}`}>{item.explain}</p>
            )}
          </div>
        ))}
      </div>
      <div className="mt-4 flex items-center gap-3">
        {!submitted ? (
          <button onClick={submit} disabled={responses.some(r => r === null)} className="bg-blue-600 text-white font-semibold px-4 py-2 rounded-lg disabled:opacity-50">Submit Quiz</button>
        ) : (
          <>
            <span className="font-semibold">Score: {score}/{questions.length}</span>
            <button onClick={() => { setResponses(Array(questions.length).fill(null)); setSubmitted(false); }} className="ml-2 text-blue-600 font-semibold">Retake</button>
          </>
        )}
      </div>
    </div>
  );
};

// --- Calculators (same as before) ---
const NPVIRRCalculator = ({ t = (s)=>s }) => {
  const [initial, setInitial] = useLocalStorage('npv_initial', 1000);
  const [rate, setRate] = useLocalStorage('npv_rate', 10);
  const [flows, setFlows] = useLocalStorage('npv_flows', [300, 300, 300, 300]);
  const npv = useMemo(() => { const r = rate / 100; let pv = -Number(initial); flows.forEach((cf, t) => { pv += Number(cf) / Math.pow(1 + r, t + 1); }); return pv; }, [initial, rate, flows]);
  const irr = useMemo(() => { const f = (r) => { let v = -Number(initial); flows.forEach((cf, t) => { v += Number(cf) / Math.pow(1 + r, t + 1); }); return v; }; let low=-0.9, high=1.0; let fLow=f(low), fHigh=f(high); if (fLow*fHigh>0) return null; for (let i=0;i<60;i++){ const mid=(low+high)/2, fMid=f(mid); if (Math.abs(fMid)<1e-7) return mid; if (fLow*fMid<0){ high=mid; fHigh=fMid; } else { low=mid; fLow=fMid; } } return (low+high)/2; }, [initial, flows]);
  const updateFlow = (i, val) => { const copy=[...flows]; copy[i]=val; setFlows(copy); };
  return (
    <div>
      <CardHeader title={t('NPV & IRR Calculator')} />
      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        <div className="space-y-4">
          <div><label className="font-semibold">{t('Initial Investment (CF0)')}</label><input type="number" value={initial} onChange={(e)=>setInitial(Number(e.target.value))} className="w-full mt-1 p-2 border rounded-lg" /></div>
          <div><label className="font-semibold">{t('Discount Rate (%)')}</label><input type="number" step="0.1" value={rate} onChange={(e)=>setRate(Number(e.target.value))} className="w-full mt-1 p-2 border rounded-lg" /></div>
          <div><label className="font-semibold">{t('Cash Flows')}</label><div className="space-y-2 mt-1">{flows.map((cf,i)=>(<div key={i} className="flex items-center gap-2"><input type="number" value={cf} onChange={(e)=>updateFlow(i, Number(e.target.value))} className="flex-1 p-2 border rounded-lg" /><button onClick={()=>setFlows(flows.filter((_,idx)=>idx!==i))} className="text-red-600">{t('Remove')}</button></div>))}<button onClick={()=>setFlows([...flows,0])} className="text-blue-600 font-semibold">+ {t('Add Cash Flow')}</button></div></div>
        </div>
        <div className="space-y-4">
          <div className="bg-blue-50 border-2 border-blue-200 rounded-xl p-4"><p className="text-gray-600">NPV</p><p className="text-3xl font-bold text-blue-700">${npv.toLocaleString(undefined,{ minimumFractionDigits:2, maximumFractionDigits:2 })}</p><p className="text-sm text-gray-500">Formula: NPV = Σ CF_t/(1+r)^t - CF_0</p></div>
          <div className="bg-green-50 border-2 border-green-200 rounded-xl p-4"><p className="text-gray-600">IRR</p><p className="text-3xl font-bold text-green-700">{irr===null?'—':((irr*100).toFixed(2)+'%')}</p><p className="text-sm text-gray-500">IRR is the discount rate making NPV = 0</p></div>
        </div>
      </div>
    </div>
  );
};

const WACCCalculator = ({ t = (s)=>s }) => {
  const [equity, setEquity] = useLocalStorage('wacc_equity', 600000);
  const [debt, setDebt] = useLocalStorage('wacc_debt', 400000);
  const [re, setRe] = useLocalStorage('wacc_re', 12);
  const [rd, setRd] = useLocalStorage('wacc_rd', 6);
  const [tax, setTax] = useLocalStorage('wacc_tax', 25);
  const wacc = useMemo(() => { const E=+equity, D=+debt, V=E+D; const ReV=+re/100, RdV=+rd/100, T=+tax/100; if(V<=0) return 0; return (E/V)*ReV + (D/V)*RdV*(1-T); }, [equity,debt,re,rd,tax]);
  return (
    <div>
      <CardHeader title={t('WACC Calculator')} />
      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        <div className="space-y-4">
          <div><label className="font-semibold">{t('Market Value of Equity (E)')}</label><input type="number" value={equity} onChange={(e)=>setEquity(Number(e.target.value))} className="w-full mt-1 p-2 border rounded-lg" /></div>
          <div><label className="font-semibold">{t('Market Value of Debt (D)')}</label><input type="number" value={debt} onChange={(e)=>setDebt(Number(e.target.value))} className="w-full mt-1 p-2 border rounded-lg" /></div>
          <div><label className="font-semibold">{t('Cost of Equity (Re, %)')}</label><input type="number" step="0.1" value={re} onChange={(e)=>setRe(Number(e.target.value))} className="w-full mt-1 p-2 border rounded-lg" /></div>
          <div><label className="font-semibold">{t('Cost of Debt (Rd, %)')}</label><input type="number" step="0.1" value={rd} onChange={(e)=>setRd(Number(e.target.value))} className="w-full mt-1 p-2 border rounded-lg" /></div>
          <div><label className="font-semibold">{t('Tax Rate (T, %)')}</label><input type="number" step="0.1" value={tax} onChange={(e)=>setTax(Number(e.target.value))} className="w-full mt-1 p-2 border rounded-lg" /></div>
        </div>
        <div className="space-y-4">
          <div className="bg-blue-50 border-2 border-blue-200 rounded-xl p-4"><p className="text-gray-600">WACC</p><p className="text-3xl font-bold text-blue-700">{(wacc*100).toFixed(2)}%</p><p className="text-sm text-gray-500">Formula: (E/V)Re + (D/V)Rd(1-T)</p></div>
        </div>
      </div>
    </div>
  );
};

const LoanAmortization = () => {
  const [principal, setPrincipal] = useLocalStorage('loan_p', 200000);
  const [rate, setRate] = useLocalStorage('loan_r', 5);
  const [years, setYears] = useLocalStorage('loan_t', 30);
  const monthlyRate = rate/100/12; const n = years*12;
  const payment = useMemo(() => { if(monthlyRate===0) return principal/n; return (principal*monthlyRate)/(1-Math.pow(1+monthlyRate, -n)); }, [principal, monthlyRate, n]);
  const schedule = useMemo(()=>{ let bal=principal; const rows=[]; for (let i=1;i<=Math.min(n,360);i++){ const interest=bal*monthlyRate; const principalPay=payment-interest; bal=Math.max(0, bal-principalPay); rows.push({month:i,payment:+payment.toFixed(2),principal:+principalPay.toFixed(2),interest:+interest.toFixed(2),balance:+bal.toFixed(2)}); if(bal<=0) break; } return rows; }, [principal, monthlyRate, n, payment]);
  const downloadCSV = () => {
    const header = ['Month','Payment','Principal','Interest','Balance'];
    const rows = schedule.map(r => [r.month, r.payment, r.principal, r.interest, r.balance]);
    const csv = [header, ...rows].map(r => r.join(',')).join('\n');
    const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url; a.download = 'loan_amortization_schedule.csv'; a.click();
    setTimeout(()=> URL.revokeObjectURL(url), 1000);
  };
  const printPDF = () => {
    const w = window.open('', '_blank');
    if (!w) return;
    const styles = `
      body { font-family: Arial, sans-serif; padding: 24px; }
      h1 { margin-bottom: 12px; }
      .meta { color: #555; margin-bottom: 16px; }
      table { border-collapse: collapse; width: 100%; }
      th, td { border: 1px solid #ddd; padding: 8px; text-align: right; }
      th { background: #1d4ed8; color: #fff; text-align: center; }
      tr:nth-child(even) { background: #f9fafb; }
    `;
    const html = `<!doctype html><html><head><meta charset="utf-8"/><title>Loan Amortization</title><style>${styles}</style></head><body>
      <h1>Loan Amortization Schedule</h1>
      <div class="meta">Loan: $${principal.toLocaleString()} | Rate: ${rate}% | Term: ${years} years | Monthly Payment: $${payment.toFixed(2)}</div>
      <table><thead><tr><th>Month</th><th>Payment</th><th>Principal</th><th>Interest</th><th>Balance</th></tr></thead><tbody>
      ${schedule.map(r => `<tr><td style="text-align:center">${r.month}</td><td>$${r.payment.toFixed(2)}</td><td>$${r.principal.toFixed(2)}</td><td>$${r.interest.toFixed(2)}</td><td>$${r.balance.toFixed(2)}</td></tr>`).join('')}
      </tbody></table>
    </body></html>`;
    w.document.open();
    w.document.write(html);
    w.document.close();
    w.focus();
    // Give the new window a tick to render before printing
    setTimeout(() => { try { w.print(); } catch (e) {} }, 300);
  };
  return (
    <div>
      <CardHeader title="Loan Amortization" />
      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        <div className="space-y-4">
          <div><label className="font-semibold">Loan Amount</label><input type="number" value={principal} onChange={(e)=>setPrincipal(Number(e.target.value))} className="w-full mt-1 p-2 border rounded-lg" /></div>
          <div><label className="font-semibold">Annual Interest Rate (%)</label><input type="number" step="0.1" value={rate} onChange={(e)=>setRate(Number(e.target.value))} className="w-full mt-1 p-2 border rounded-lg" /></div>
          <div><label className="font-semibold">Term (years)</label><input type="number" value={years} onChange={(e)=>setYears(Number(e.target.value))} className="w-full mt-1 p-2 border rounded-lg" /></div>
          <div className="bg-blue-50 border-2 border-blue-200 rounded-xl p-4"><p className="text-gray-600">Monthly Payment</p><p className="text-3xl font-bold text-blue-700">${payment.toFixed(2)}</p></div>
        </div>
        <div>
          <div style={{ width: '100%', height: 260 }}>
            <ResponsiveContainer>
              <BarChart data={schedule.slice(0, 24)} margin={{ top: 10, right: 20, left: 0, bottom: 10 }}>
                <CartesianGrid strokeDasharray="3 3" />
                <XAxis dataKey="month" />
                <YAxis />
                <Tooltip />
                <Legend />
                <Bar dataKey="principal" stackId="a" fill="#4A90E2" name="Principal" />
                <Bar dataKey="interest" stackId="a" fill="#F5A623" name="Interest" />
              </BarChart>
            </ResponsiveContainer>
          </div>
          <p className="text-sm text-gray-500 mt-2">First 24 months breakdown. Full schedule available below.</p>
          <div className="mt-3 flex flex-wrap gap-3">
            <button onClick={downloadCSV} className="px-3 py-2 rounded-lg border bg-white hover:bg-gray-50">Download Excel (CSV)</button>
            <button onClick={printPDF} className="px-3 py-2 rounded-lg border bg-white hover:bg-gray-50">Download PDF</button>
          </div>
        </div>
      </div>
    </div>
  );
};

const BondValuation = () => {
  const [face, setFace] = useLocalStorage('bond_face', 1000);
  const [coupon, setCoupon] = useLocalStorage('bond_coupon', 5);
  const [yieldReq, setYieldReq] = useLocalStorage('bond_yield', 6);
  const [years, setYears] = useLocalStorage('bond_years', 10);
  const [freq, setFreq] = useLocalStorage('bond_freq', 2);
  const price = useMemo(()=>{ const c=(coupon/100)*face/freq; const y=yieldReq/100/freq; const n=years*freq; if(y===0) return c*n+face; const pvC = c*(1-Math.pow(1+y, -n))/y; const pvF = face*Math.pow(1+y, -n); return pvC+pvF; }, [face,coupon,yieldReq,years,freq]);
  return (
    <div>
      <CardHeader title="Bond Valuation" />
      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        <div className="space-y-4">
          <Labeled number label="Face Value" tooltip="Redemption value at maturity." value={face} onChange={setFace} />
          <Labeled number step={0.1} label="Coupon Rate (%)" tooltip="Annual nominal coupon rate." value={coupon} onChange={setCoupon} />
          <Labeled number step={0.1} label="Required Yield (%)" tooltip="Market required nominal yield (APR)." value={yieldReq} onChange={setYieldReq} />
          <Labeled number label="Years to Maturity" value={years} onChange={setYears} />
          <div>
            <label className="font-semibold flex items-center gap-2">Frequency <TooltipInfo text="Coupons per year (e.g., 1=Annual, 2=Semiannual)" /></label>
            <select value={freq} onChange={(e)=>setFreq(Number(e.target.value))} className="w-full mt-1 p-2 border rounded-lg" aria-label="Coupon frequency">
              <option value={1}>Annual</option>
              <option value={2}>Semiannual</option>
              <option value={4}>Quarterly</option>
            </select>
          </div>
        </div>
        <div className="space-y-4">
          <div className="bg-blue-50 border-2 border-blue-200 rounded-xl p-4"><p className="text-gray-600">Price</p><p className="text-3xl font-bold text-blue-700">${price.toFixed(2)}</p><p className="text-sm text-gray-500">PV of coupons + PV of face at required yield</p></div>
        </div>
      </div>
    </div>
  );
};

const DDMCalculator = () => {
  const [dividend1, setDividend1] = useLocalStorage('ddm_d1', 2.0);
  const [g, setG] = useLocalStorage('ddm_g', 4.0);
  const [r, setR] = useLocalStorage('ddm_r', 9.0);
  const price = useMemo(()=>{ const gr=g/100, rr=r/100; if(rr<=gr) return null; return dividend1/(rr-gr); }, [dividend1,g,r]);
  return (
    <div>
      <CardHeader title="DDM (Dividend Discount)" />
      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        <div className="space-y-4">
          <Labeled number step={0.01} label="Next Dividend (D1)" tooltip="Dividend expected next period." value={dividend1} onChange={setDividend1} />
          <Labeled number step={0.1} label="Growth Rate g (%)" tooltip="Perpetual growth rate." value={g} onChange={setG} />
          <Labeled number step={0.1} label="Required Return r (%)" tooltip="Investor's required return." value={r} onChange={setR} />
        </div>
        <div className="space-y-4">
          <div className="bg-blue-50 border-2 border-blue-200 rounded-xl p-4"><p className="text-gray-600">Intrinsic Value</p><p className="text-3xl font-bold text-blue-700">{price===null?'—':('$'+price.toFixed(2))}</p><p className="text-sm text-gray-500">Formula: P0 = D1 / (r − g), valid when r &gt; g</p></div>
        </div>
      </div>
    </div>
  );
};

const BetaCalculator = () => {
  const [rho, setRho] = useLocalStorage('beta_rho', 0.8);
  const [sigmaS, setSigmaS] = useLocalStorage('beta_sigmas', 0.25);
  const [sigmaM, setSigmaM] = useLocalStorage('beta_sigmam', 0.18);
  const beta = useMemo(()=>{ if(sigmaM===0) return null; return rho*(sigmaS/sigmaM); }, [rho,sigmaS,sigmaM]);
  return (
    <div>
      <CardHeader title="Beta (CAPM)" />
      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        <div className="space-y-4">
          <Labeled number step={0.01} label="Correlation (ρ)" tooltip="Correlation of stock and market returns." value={rho} onChange={setRho} />
          <Labeled number step={0.01} label="Stock Volatility σs" tooltip="Std dev of stock returns (e.g., 0.25 for 25%)." value={sigmaS} onChange={setSigmaS} />
          <Labeled number step={0.01} label="Market Volatility σm" tooltip="Std dev of market returns." value={sigmaM} onChange={setSigmaM} />
        </div>
        <div className="space-y-4">
          <div className="bg-blue-50 border-2 border-blue-200 rounded-xl p-4"><p className="text-gray-600">Beta</p><p className="text-3xl font-bold text-blue-700">{beta===null?'—':beta.toFixed(2)}</p><p className="text-sm text-gray-500">β = ρ × (σs / σm)</p></div>
        </div>
      </div>
    </div>
  );
};

const CapitalBudgetingInteractive = () => {
  const [initial, setInitial] = useLocalStorage('capex_initial', 1000);
  const [flows, setFlows] = useLocalStorage('capex_flows', [300, 300, 300, 300, 300]);
  const rates = useMemo(()=>Array.from({length:16},(_,i)=>i*2),[]);
  const profile = useMemo(()=> rates.map(r=>{ const rr=r/100; let pv=-Number(initial); flows.forEach((cf,t)=>{ pv+=Number(cf)/Math.pow(1+rr, t+1); }); return { rate:r, npv:+pv.toFixed(2)};}), [initial,flows,rates]);
  const irr = useMemo(()=>{ const f=(r)=>{ let v=-Number(initial); flows.forEach((cf,t)=>{ v+=Number(cf)/Math.pow(1+r, t+1); }); return v; }; let low=-0.9, high=1.0; let fLow=f(low), fHigh=f(high); if(fLow*fHigh>0) return null; for(let i=0;i<60;i++){ const mid=(low+high)/2, fMid=f(mid); if(Math.abs(fMid)<1e-7) return mid; if(fLow*fMid<0){ high=mid; fHigh=fMid; } else { low=mid; fLow=fMid; } } return (low+high)/2; }, [initial,flows]);
  const payback = useMemo(()=>{ let cum=-Number(initial); for(let i=0;i<flows.length;i++){ cum+=Number(flows[i]); if(cum>=0){ const prevCum=cum-Number(flows[i]); const rem=-prevCum; return i+(rem/Number(flows[i])); } } return null; }, [initial,flows]);
  const updateFlow = (i,val)=>{ const copy=[...flows]; copy[i]=val; setFlows(copy); };
  return (
    <div className="space-y-6">
      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        <div className="space-y-4">
          <Labeled number label="Initial Investment (CF0)" tooltip="Enter as positive; treated as outflow." value={initial} onChange={setInitial} />
          <div>
            <label className="font-semibold flex items-center gap-2">Cash Flows <TooltipInfo text="Enter CF1..CFn; add rows as needed." /></label>
            <div className="space-y-2 mt-1">
              {flows.map((cf,i)=>(
                <div key={i} className="flex items-center gap-2">
                  <input type="number" value={cf} onChange={(e)=>updateFlow(i, Number(e.target.value))} className="flex-1 p-2 border rounded-lg" />
                  <button onClick={()=>setFlows(flows.filter((_,idx)=>idx!==i))} className="text-red-600">Remove</button>
                </div>
              ))}
              <button onClick={()=>setFlows([...flows,0])} className="text-blue-600 font-semibold">+ Add Cash Flow</button>
            </div>
          </div>
        </div>
        <div className="space-y-4">
          <div className="grid grid-cols-2 gap-4">
            <div className="bg-blue-50 border-2 border-blue-200 rounded-xl p-4"><p className="text-gray-600">IRR</p><p className="text-3xl font-bold text-blue-700">{irr===null?'—':(irr*100).toFixed(2)+'%'}</p></div>
            <div className="bg-green-50 border-2 border-green-200 rounded-xl p-4"><p className="text-gray-600">Payback (yrs)</p><p className="text-3xl font-bold text-green-700">{payback===null?'—':payback.toFixed(2)}</p></div>
          </div>
          <div style={{ width: '100%', height: 260 }} role="img" aria-label="NPV profile chart">
            <ResponsiveContainer>
              <LineChart data={profile} margin={{ top: 10, right: 20, left: 10, bottom: 10 }}>
                <CartesianGrid strokeDasharray="3 3" />
                <XAxis dataKey="rate" label={{ value: 'Discount Rate (%)', position: 'insideBottom', offset: -5 }} />
                <YAxis label={{ value: 'NPV', angle: -90, position: 'insideLeft' }} />
                <Tooltip />
                <Legend />
                <Line type="monotone" dataKey="npv" stroke="#4A90E2" strokeWidth={2} dot={false} name="NPV" />
              </LineChart>
            </ResponsiveContainer>
          </div>
        </div>
      </div>
    </div>
  );
};

const QuizCapitalBudgeting = () => {
  const qs = [
    { q: 'If NPV is positive at r = 12%, what does that imply?', options: ['Project destroys value', 'Project creates value', 'IRR < 12%', 'Cash flows are negative'], a: 1, ex: 'Positive NPV means value creation at that discount rate.' },
    { q: 'Payback period ignores:', options: ['Time value of money', 'Project risk', 'Cash flows after cutoff', 'All of the above'], a: 3, ex: 'Classic payback ignores TVM and post-cutoff cash flows.' }
  ];
  const [resp, setResp] = useLocalStorage('capex_quiz_r', Array(qs.length).fill(null));
  const [sub, setSub] = useLocalStorage('capex_quiz_s', false);
  const score = useMemo(() => qs.reduce((s, q, i) => s + (resp[i] === q.a ? 1 : 0), 0), [resp]);
  return (
    <div className="bg-white rounded-xl border p-4">
      <h3 className="text-xl font-bold mb-4">Check Your Understanding: Capital Budgeting</h3>
      <div className="space-y-4">
        {qs.map((item, idx) => (
          <div key={idx} className="p-3 rounded-lg border">
            <p className="font-medium mb-2">{idx+1}. {item.q}</p>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
              {item.options.map((opt, oi) => (
                <button key={oi} onClick={() => !sub && setResp(p => { const c=[...p]; c[idx]=oi; return c; })} className={`text-left px-3 py-2 rounded-lg border ${resp[idx]===oi?'border-blue-500':'border-gray-200'} ${sub && oi===item.a ? 'bg-green-50 border-green-500':''} ${sub && resp[idx]===oi && oi!==item.a ? 'bg-red-50 border-red-500':''}`}>{opt}</button>
              ))}
            </div>
            {sub && <p className={`mt-2 text-sm ${resp[idx]===item.a?'text-green-700':'text-red-700'}`}>{item.ex}</p>}
          </div>
        ))}
      </div>
      <div className="mt-3 flex items-center gap-3">{!sub ? <button onClick={() => { setSub(true); logActivity('quiz_submit', { quiz: 'CapitalBudgeting', score }); }} disabled={resp.some(r=>r===null)} className="bg-blue-600 text-white px-4 py-2 rounded-lg disabled:opacity-50">Submit Quiz</button> : <><span className="font-semibold">Score: {score}/{qs.length}</span><button onClick={()=>{setResp(Array(qs.length).fill(null)); setSub(false);}} className="text-blue-600 font-semibold">Retake</button></>}</div>
    </div>
  );
};

const NewsSnippetCapitalBudgeting = () => (
  <Card>
    <CardHeader title="In the News" />
    <p className="text-gray-600">Many firms assess capital projects (like new data centers) using NPV and IRR to allocate capital efficiently.</p>
    <a href="https://www.wsj.com/" target="_blank" rel="noreferrer" className="text-blue-600 font-semibold mt-2 inline-block">Read an example →</a>
  </Card>
);

// --- Module 3: Risk & Return ---
const RiskReturnInteractive = () => {
  const [rf, setRf] = useLocalStorage('rr_rf', 2.0);
  const [rm, setRm] = useLocalStorage('rr_rm', 8.0);
  const [beta, setBeta] = useLocalStorage('rr_beta', 1.1);
  const er = useMemo(() => rf + beta * (rm - rf), [rf, rm, beta]);
  const frontier = useMemo(() => {
    const data = [];
    for (let s = 5; s <= 25; s += 2) {
      const ret = 2 + (s - 5) * 0.4; // toy efficient frontier
      data.push({ risk: s, ret: parseFloat(ret.toFixed(2)) });
    }
    return data;
  }, []);
  return (
    <div className="space-y-6">
      <p className="text-gray-600">Use CAPM to estimate the expected return given beta, market return, and risk-free rate. The chart illustrates a toy efficient frontier.</p>
      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        <div className="space-y-4">
          <Labeled number step={0.1} label="Risk-free rate (Rf, %)" value={rf} onChange={setRf} />
          <Labeled number step={0.1} label="Market return (Rm, %)" value={rm} onChange={setRm} />
          <Labeled number step={0.01} label="Beta (β)" value={beta} onChange={setBeta} />
          <div className="bg-blue-50 border-2 border-blue-200 rounded-xl p-4">
            <p className="text-gray-600">Expected Return (CAPM)</p>
            <p className="text-3xl font-bold text-blue-700">{er.toFixed(2)}%</p>
            <p className="text-sm text-gray-500">E(Ri) = Rf + β (Rm − Rf)</p>
          </div>
        </div>
        <div style={{ width: '100%', height: 260 }}>
          <ResponsiveContainer>
            <LineChart data={frontier} margin={{ top: 10, right: 20, left: 10, bottom: 10 }}>
              <CartesianGrid strokeDasharray="3 3" />
              <XAxis dataKey="risk" label={{ value: 'Risk (σ, %)', position: 'insideBottom', offset: -5 }} />
              <YAxis label={{ value: 'Return (%)', angle: -90, position: 'insideLeft' }} />
              <Tooltip />
              <Legend />
              <Line type="monotone" dataKey="ret" stroke="#4A90E2" strokeWidth={2} dot={false} name="Efficient Frontier" />
            </LineChart>
          </ResponsiveContainer>
        </div>
      </div>
    </div>
  );
};

// --- Module 4: Cost of Capital ---
const CostOfCapitalInteractive = () => {
  const [E, setE] = useLocalStorage('coc_E', 600);
  const [D, setD] = useLocalStorage('coc_D', 400);
  const [Re, setRe] = useLocalStorage('coc_Re', 12);
  const [Rd, setRd] = useLocalStorage('coc_Rd', 6);
  const [T, setT] = useLocalStorage('coc_T', 25);
  const wacc = useMemo(() => { const V=E+D; if(!V) return 0; return (E/V)*(Re/100) + (D/V)*(Rd/100)*(1 - T/100); }, [E,D,Re,Rd,T]);
  return (
    <div className="space-y-6">
      <p className="text-gray-600">Weighted Average Cost of Capital (WACC) blends the cost of equity and debt, net of the interest tax shield.</p>
      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        <div className="space-y-4">
          <Labeled number label="Equity (E)" value={E} onChange={setE} />
          <Labeled number label="Debt (D)" value={D} onChange={setD} />
          <Labeled number step={0.1} label="Cost of Equity Re (%)" value={Re} onChange={setRe} />
          <Labeled number step={0.1} label="Cost of Debt Rd (%)" value={Rd} onChange={setRd} />
          <Labeled number step={0.1} label="Tax Rate T (%)" value={T} onChange={setT} />
        </div>
        <div className="space-y-4">
          <div className="bg-blue-50 border-2 border-blue-200 rounded-xl p-4">
            <p className="text-gray-600">WACC</p>
            <p className="text-3xl font-bold text-blue-700">{(wacc*100).toFixed(2)}%</p>
            <p className="text-sm text-gray-500">(E/V)Re + (D/V)Rd(1−T)</p>
          </div>
        </div>
      </div>
    </div>
  );
};

// --- Module 5: Capital Structure ---
const CapitalStructureInteractive = () => {
  const [debtRatio, setDebtRatio] = useLocalStorage('cs_dr', 40); // %
  const [ReBase, setReBase] = useLocalStorage('cs_re', 10);
  const [RdBase, setRdBase] = useLocalStorage('cs_rd', 5);
  const [tax, setTax] = useLocalStorage('cs_tax', 25);
  const wacc = useMemo(() => {
    const D = debtRatio/100, E = 1-D;
    const Re = ReBase + (debtRatio-40)*0.05; // toy leverage effect
    const Rd = RdBase + (debtRatio-40)*0.03;
    return E*(Re/100) + D*(Rd/100)*(1 - tax/100);
  }, [debtRatio, ReBase, RdBase, tax]);
  const chart = useMemo(()=>{
    const pts=[]; for(let r=0; r<=80; r+=5){ const D=r/100, E=1-D; const Re=ReBase+(r-40)*0.05; const Rd=RdBase+(r-40)*0.03; const val = E*(Re/100)+D*(Rd/100)*(1-tax/100); pts.push({ debt:r, wacc:+(val*100).toFixed(2)});} return pts;
  }, [ReBase,RdBase,tax]);
  return (
    <div className="space-y-6">
      <p className="text-gray-600">Explore how leverage (debt ratio) can influence WACC in a simplified trade-off view.</p>
      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        <div className="space-y-4">
          <Labeled number step={1} label="Debt ratio (%)" value={debtRatio} onChange={setDebtRatio} />
          <Labeled number step={0.1} label="Base Re (%)" value={ReBase} onChange={setReBase} />
          <Labeled number step={0.1} label="Base Rd (%)" value={RdBase} onChange={setRdBase} />
          <Labeled number step={0.1} label="Tax rate T (%)" value={tax} onChange={setTax} />
          <div className="bg-blue-50 border-2 border-blue-200 rounded-xl p-4">
            <p className="text-gray-600">WACC</p>
            <p className="text-3xl font-bold text-blue-700">{(wacc*100).toFixed(2)}%</p>
          </div>
        </div>
        <div style={{ width: '100%', height: 260 }}>
          <ResponsiveContainer>
            <LineChart data={chart} margin={{ top: 10, right: 20, left: 10, bottom: 10 }}>
              <CartesianGrid strokeDasharray="3 3" />
              <XAxis dataKey="debt" label={{ value: 'Debt ratio (%)', position: 'insideBottom', offset: -5 }} />
              <YAxis label={{ value: 'WACC (%)', angle: -90, position: 'insideLeft' }} />
              <Tooltip />
              <Legend />
              <Line type="monotone" dataKey="wacc" stroke="#4A90E2" strokeWidth={2} dot={false} name="WACC" />
            </LineChart>
          </ResponsiveContainer>
        </div>
      </div>
    </div>
  );
};

// --- UI UTILITIES ---
const Card = ({ children, className = '' }) => (
  <div
    className={`bg-white rounded-2xl shadow-lg p-6 border border-gray-100 hover:shadow-xl transition-all duration-200 ${className}`}
    style={{ backgroundImage: 'radial-gradient(ellipse at top left, rgba(99,102,241,0.06), transparent 40%), radial-gradient(ellipse at bottom right, rgba(16,185,129,0.06), transparent 40%)' }}
  >
    {children}
  </div>
);

const CardHeader = ({ title, icon: Icon }) => (
  <div className="flex items-center justify-between mb-5">
    <h2 className="text-2xl md:text-3xl font-extrabold tracking-tight bg-clip-text text-transparent bg-gradient-to-r from-indigo-600 to-emerald-500">
      {title}
    </h2>
    {Icon && <Icon className="w-6 h-6 text-indigo-400" />}
  </div>
);

const FeatureCard = ({ icon: Icon, title, onClick }) => (
  <button
    onClick={onClick}
    className="p-5 rounded-2xl text-center bg-white border border-gray-100 hover:border-indigo-200 hover:shadow-lg transition-all group"
  >
    <div className="w-14 h-14 rounded-xl mx-auto mb-3 flex items-center justify-center bg-gradient-to-br from-indigo-600 to-emerald-500 text-white shadow-md group-hover:shadow-lg group-hover:scale-[1.02] transition-transform">
      <Icon className="w-7 h-7" />
    </div>
    <p className="font-bold text-gray-800">{title}</p>
  </button>
);

const GlobalSearch = ({ onSubmit }) => {
  const [q, setQ] = useState('');
  return (
    <div className="relative hidden lg:block">
      <input
        value={q}
        onChange={(e)=>setQ(e.target.value)}
        onKeyDown={(e)=>{ if(e.key==='Enter') onSubmit && onSubmit(q); }}
        type="text"
        placeholder="Search..."
        className="pl-10 pr-4 py-2 border rounded-full w-64 focus:outline-none focus:ring-2 focus:ring-indigo-500"
        aria-label="Global search"
      />
      <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-indigo-400"/>
    </div>
  );
};

const Labeled = ({ label, tooltip, value, onChange, number = false, step }) => (
  <div>
    <label className="font-semibold flex items-center gap-2">{label} {tooltip && <TooltipInfo text={tooltip} />}</label>
    <input type={number? 'number':'text'} step={step} value={value} onChange={(e)=>onChange(number? Number(e.target.value): e.target.value)} className="w-full mt-1 p-2 border rounded-lg" aria-label={label} />
  </div>
);

const TooltipInfo = ({ text }) => (
  <span className="inline-flex items-center" tabIndex={0} aria-label={text} title={text}>
    <Info className="w-4 h-4 text-gray-400" />
  </span>
);

// Custom animations (guarded injection to avoid runtime issues)
try {
  if (typeof document !== 'undefined' && !document.getElementById('dynamic-bg-style')) {
    const style = document.createElement('style');
    style.id = 'dynamic-bg-style';
    style.textContent = `
  @keyframes marquee { 0% { transform: translateX(0%); } 100% { transform: translateX(-50%); } }
  .animate-marquee { animation: marquee 40s linear infinite; }
  .fade-in { animation: fadeInAnimation 0.5s ease-in-out; }
  @keyframes fadeInAnimation { 0% { opacity: 0; transform: translateY(10px); } 100% { opacity: 1; transform: translateY(0); } }

  /* iOS safe-area support */
  .ios-safe { padding-top: calc(env(safe-area-inset-top, 0px) + 0.5rem); padding-bottom: calc(env(safe-area-inset-bottom, 0px) + 0.5rem); }

  /* Scroll snap experience for mobile */
  .snap-container { scroll-snap-type: none; }
  .snap-card { scroll-snap-align: start; }

  /* Hover/press pop effect (works on touch with active) */
  .hover-pop { transition: transform 220ms ease, box-shadow 220ms ease; }
  .hover-pop:hover { transform: translateY(-2px) scale(1.01); }
  .hover-pop:active { transform: translateY(0) scale(0.99); }

  /* Entrance animations */
  @keyframes slideUpFade {
    0% { opacity: 0; transform: translateY(16px); }
    100% { opacity: 1; transform: translateY(0); }
  }
  .mobile-tab-entrance { animation: slideUpFade 320ms ease-out; }
  .sim-form-entrance { animation: slideUpFade 380ms ease-out; }

  /* Trendy animated background */
  .bg-gradient-animate {
    background: radial-gradient(1200px 800px at 10% 10%, rgba(99,102,241,0.18), transparent 40%),
                radial-gradient(900px 700px at 90% 20%, rgba(16,185,129,0.18), transparent 45%),
                linear-gradient(180deg, #f8fafc 0%, #ffffff 100%);
    position: fixed;
    pointer-events: none;
  }
  .bg-blob {
    position: absolute;
    filter: blur(40px);
    opacity: 0.5;
    border-radius: 9999px;
    will-change: transform, opacity;
    animation: floaty 26s ease-in-out infinite;
    pointer-events: none;
  }
  .bg-blob-1 { width: 420px; height: 420px; left: -120px; top: 60px; background: #a78bfa; animation-delay: -2s; }
  .bg-blob-2 { width: 520px; height: 520px; right: -160px; top: 140px; background: #34d399; animation-delay: 0s; }
  .bg-blob-3 { width: 360px; height: 360px; left: 20%; bottom: -120px; background: #22d3ee; animation-delay: -4s; }
  @keyframes floaty {
    0% { transform: translateY(0) translateX(0) scale(1); }
    25% { transform: translateY(-20px) translateX(10px) scale(1.05); }
    50% { transform: translateY(0) translateX(-10px) scale(1); }
    75% { transform: translateY(20px) translateX(6px) scale(0.98); }
    100% { transform: translateY(0) translateX(0) scale(1); }
  }
`;
    document.head.appendChild(style);
  }
} catch {}
